import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import InfoPopup from '../components/InfoPopup';
import { useNavigate, useLocation } from 'react-router-dom';
import ProductCartCounter from '../components/ProductCartCounter';
import RecommendedProductCard from '../components/RecommendedProductCard';
import CheckoutModal from '../components/CheckoutModal';
import { useSkinSession } from '../context/SkinSessionContext';
import { getSessionIdFromUrl, getSessionId, pathWithSessionId } from '../utils/sessionManager';
import { saveSkinFlowState, getSkinFlowState, clearSkinFlowState } from '../utils/skinFlowState';
import { getApiBaseUrl } from '../utils/apiConfig';
import { sanitizeHtml, stripHtml } from '../utils/sanitizeHtml';
import { SEGMENTATION_API_BASE, normalizeSegmentationOption, parseFeatureToggle, resolveSegmentationProductActions, isValidProductUrl } from '../utils/segmentation';
import { getConditionDisplayName } from '../config/skinConditionDisplayNames';
import { getConditionDescription } from '../config/skinConditionDescriptions';
import { useSkinAnalysisPlan } from '../context/SkinAnalysisPlanContext';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { flattenRecommendedProducts, attachRoutineSessions, dedupeProductsById } from '../utils/normalizeRecommendedProducts';
import { buildRecommendableConditions, buildRecommendationPayloadScores } from '../utils/recommendationSeverity';
import { recordSkinFlowStage } from '../utils/dataSaver';
import { normalizeConditionName } from '../utils/skinConditionNormalization';
import { getDetectionsToDraw, buildSeverityBarGradientStyleVars, canonicalSkinConditionName, isSkinConditionName } from '../utils/skinResultsUtils';
import { getSeverityBarLabels } from '../utils/severityBarLabels';
import { getPreviousSkinAnalysis } from '../utils/previousSkinAnalysis';
import { fetchSkinConcernMask } from '../utils/lazySkinMask';
import FaceRegionMask from '../components/FaceRegionMask';
import '../styles/PageBackground.css';
import '../styles/ActionButtons.css';
import './SkinRoutinePage.css';
import './ProductRecommendationsPage.css';

const toYesNoValue = (value) => {
  if (value == null) return null;
  return parseFeatureToggle(value) ? 'yes' : 'no';
};

const isSkinQualityName = (name) => isSkinConditionName(name);

// Helper function to convert BGR to RGB
const bgrToRgb = (b, g, r) => `rgb(${r}, ${g}, ${b})`;

// BGR color definitions - match SkinResultsPage; high visibility across all skin tones
const BGR_COLORS_BY_ID = {
  0: [255, 191, 0],     // Wrinkles - cyan
  1: [50, 205, 50],     // Dry skin - lime green
  2: [255, 190, 0],     // Oily skin - dominant cyan/teal
  3: [255, 0, 255],     // Dark circle - magenta
  4: [0, 0, 255],       // Acne - red
  5: [60, 20, 220],     // Red spot - crimson
  6: [0, 69, 255],      // Pigmentation - red-orange
  7: [128, 128, 128],   // Unknown - mid gray
  8: [100, 100, 100],   // Mole - dark gray
  9: [180, 105, 255],   // Dark spots - hot pink
  10: [211, 0, 148],    // Whiteheads - dark violet
  11: [255, 0, 255],    // Crow's feet - magenta
  12: [200, 200, 0],    // Open Pores - teal
  13: [225, 105, 65],   // Blackheads - royal blue
  14: [240, 32, 160],   // Clogged Pores - purple
  15: [214, 112, 218]   // Eye Bags - orchid
};

// Helper function to get color by class_id (matching SkinResultsPage - high visibility on all skin tones)
const getColorByClassId = (classId) => {
  if (BGR_COLORS_BY_ID[classId] !== undefined) {
    const [b, g, r] = BGR_COLORS_BY_ID[classId];
    return bgrToRgb(b, g, r);
  }
  return 'rgb(160, 32, 240)'; // Default - purple
};

// Class colors map - must match SkinResultsPage exactly (visibility across all skin tones)
const CLASS_COLORS_MAP = {
  'Wrinkles': 'rgb(0, 191, 255)',
  'Dry skin': 'rgb(0, 190, 255)',
  'Oily skin': 'rgb(0, 190, 255)',
  'Dark circle': 'rgb(255, 0, 255)',
  'Acne': 'rgb(255, 0, 0)',
  'Red spot': 'rgb(220, 20, 60)',
  'Pigmentation': 'rgb(255, 69, 0)',
  'Unknown': 'rgb(128, 128, 128)',
  'Mole': 'rgb(100, 100, 100)',
  'Dark spots': 'rgb(255, 105, 180)',
  'Whiteheads': 'rgb(148, 0, 211)',
  "Crow's feet": 'rgb(255, 0, 255)',
  'Open Pores': 'rgb(0, 200, 200)',
  'Blackheads': 'rgb(65, 105, 225)',
  'Clogged Pores': 'rgb(160, 32, 240)',
  'Eye Bags': 'rgb(218, 112, 214)',
  'Hydration': 'rgb(0, 200, 120)',
  'Oxygen': 'rgb(100, 149, 237)',
  'Smoothness': 'rgb(218, 112, 214)',
  'Dullness': 'rgb(205, 133, 0)',
  'UnevenSkin': 'rgb(255, 99, 71)'
};

// Helper function to get outline color based on score range
const getScoreOutlineColor = (score) => {
  if (score >= 0 && score <= 39) {
    return 'critical'; // Red
  } else if (score >= 40 && score <= 59) {
    return 'poor'; // Orange
  } else if (score >= 60 && score <= 74) {
    return 'fair'; // Yellow/Amber
  } else if (score >= 75 && score <= 89) {
    return 'good'; // Light Green
  } else {
    return 'excellent'; // Green
  }
};

const normalizeDescriptionMap = (value) => {
  if (!value || typeof value !== 'object') return {};
  const map = {};
  Object.entries(value).forEach(([k, v]) => {
    const key = normalizeConditionName(String(k || '').trim()) || String(k || '').trim();
    const val = String(v || '').trim();
    if (!key || !val) return;
    map[key] = val;
  });
  return map;
};

const getSkinConditionKeyCandidates = (conditionName) => {
  const raw = String(conditionName || '').trim();
  if (!raw) return [];

  const underscored = raw.toLowerCase().replace(/\s+/g, '_');
  const compact = raw.toLowerCase().replace(/[\s_-]+/g, '');
  const candidates = [underscored, compact, raw];

  if (
    compact === 'unevenskin'
    || compact === 'unevenskintone'
    || compact === 'uneventone'
    || compact === 'eventone'
  ) {
    candidates.unshift('uneven_skintone');
  }

  return Array.from(new Set(candidates));
};

const formatProductPrice = (value) => {
  if (value == null || value === '') return '₹0.00';
  const normalized = Number(String(value).replace(/[^0-9.-]/g, ''));
  if (Number.isNaN(normalized)) return '₹0.00';
  return `₹${normalized.toFixed(2)}`;
};

const SkinRoutinePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, sessionLandingPath } = useSkinSession();
  const { ENABLE_RADAR_CHART } = useSkinAnalysisPlan();
  // Temporarily disable all AI generation and UI blocks for skin flow.
  const SKIP_AI = true;

  const sessionIdFromState = location.state?.sessionId || null;
  const sessionIdFromUrl = getSessionIdFromUrl(location);
  const sessionIdFromStorage = getSessionId();
  const sessionId = sessionIdFromState || sessionIdFromUrl || sessionIdFromStorage || null;

  const effectiveState = useMemo(() => {
    if (location.state && (location.state.skinResults || location.state.routineData || location.state.userData)) return location.state;
    if (!sessionId) return null;
    return getSkinFlowState(sessionId);
  }, [location.state, sessionId]);

  const userDataFromState = effectiveState?.userData || location.state?.userData || null;
  const capturedImageFromState = effectiveState?.capturedImage || location.state?.capturedImage || null;
  const skinResultsFromState = effectiveState?.skinResults || location.state?.skinResults || null;
  const skinGoalDataFromState = effectiveState?.skinGoalData || location.state?.skinGoalData || null;
  const visibleConditionNames = effectiveState?.visibleConditionNames ?? location.state?.visibleConditionNames ?? null;
  const routineDataFromState = effectiveState?.routineData || location.state?.routineData || null;

  const getStoredRoutineData = () => {
    if (routineDataFromState) return routineDataFromState;
    if (sessionId) {
      try {
        const stored = localStorage.getItem(`skin_routine_${sessionId}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          const currentGoals = skinGoalDataFromState?.goals ?? [];
          const cachedGoals = parsed?.generatedWithGoals ?? [];
          const goalsMatch = JSON.stringify([...currentGoals].sort()) === JSON.stringify([...cachedGoals].sort());
          if (goalsMatch) return parsed;
        }
      } catch (e) {
      }
    }
    return null;
  };

  const storedData = getStoredRoutineData();
  const [summary, setSummary] = useState(storedData?.summary || '');
  const [routine, setRoutine] = useState(storedData?.routine || { morning: [], evening: [] });
  const [skincareTips, setSkincareTips] = useState(storedData?.skincare_tips || []);
  // skin age generation removed from skin flow per design
  const [isLoading, setIsLoading] = useState(!storedData);
  const [error, setError] = useState(null);
  // When "no", hide AI Summary section and do not show/generate it
  const [aiGeneratedSummary, setAiGeneratedSummary] = useState(null);
  const [downloadPdfTemplate, setDownloadPdfTemplate] = useState(null);
  const [aiMode, setAiMode] = useState(null);
  const [productSuggestionEnabled, setProductSuggestionEnabled] = useState(true);
  const [advancedGraphicalChartsEnabled, setAdvancedGraphicalChartsEnabled] = useState(true);
  const [showSkinConcerns, setShowSkinConcerns] = useState(true);
  const [showSkinConditions, setShowSkinConditions] = useState(true);
  // final report persistence removed for skin flow; state not needed
  const [segmentationConfigLoaded, setSegmentationConfigLoaded] = useState(!token);
  const [segmentationAddToCart, setSegmentationAddToCart] = useState(true);
  const [segmentationProductLink, setSegmentationProductLink] = useState(true);
  const [productRecommendationSource, setProductRecommendationSource] = useState('database');
  const [productsPageUrl, setProductsPageUrl] = useState('');
  const [productsPageUrls, setProductsPageUrls] = useState([]);
  // Recommended products (cart + checkout on this page)
  const cartItemsFromState = effectiveState?.cartItems ?? location.state?.cartItems ?? null;
  const updatedCartFromCheckout = location.state?.updatedCartFromCheckout || false;
  const [cartItems, setCartItems] = useState(cartItemsFromState || location.state?.cartItems || {});
  const [recommendations, setRecommendations] = useState(null);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [productsStageDone, setProductsStageDone] = useState(false);
  const [deepDiveStageDone, setDeepDiveStageDone] = useState(false);
  const [uspPopup, setUspPopup] = useState({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });
  const [productDetailPopup, setProductDetailPopup] = useState({ open: false, product: null, recommendationLine: '' });
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const [showHomeConfirmPopup, setShowHomeConfirmPopup] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isGeneratingPdf] = useState(false);
  const [previousSkinData, setPreviousSkinData] = useState(() => effectiveState?.previousSkinData || null);
  const [, setPreviousSkinLoading] = useState(false);
  const [aiConditionDescriptions, setAiConditionDescriptions] = useState(() => {
    const s = effectiveState?.aiConditionDescriptions ?? location.state?.aiConditionDescriptions;
    return (s && typeof s === 'object') ? s : {};
  });
  const [isLoadingConditionDescriptions, setIsLoadingConditionDescriptions] = useState(false);
  const [selectedConcernName, setSelectedConcernName] = useState(() => effectiveState?.selectedCondition || null);
  const [animatedConcernName, setAnimatedConcernName] = useState(null);
  const [radarComparisonTab, setRadarComparisonTab] = useState('after');
  const [radarConcernModal, setRadarConcernModal] = useState({ open: false, index: 0 });
  const [selectedConcernView, setSelectedConcernView] = useState(() => {
    const view = effectiveState?.imageView;
    return view === 'left' || view === 'right' || view === 'front' ? view : 'front';
  });
  const [isConcernTextExpanded, setIsConcernTextExpanded] = useState(false);
  const [isConcernInsightsOpen, setIsConcernInsightsOpen] = useState(true);
  const [selectedConcernPreviewImage, setSelectedConcernPreviewImage] = useState(
    () => skinResultsFromState?.markedImage || capturedImageFromState || null
  );
  /** Lazily fetched concern masks keyed by backend condition key (hydration, …). */
  const [conditionMaskOverrides, setConditionMaskOverrides] = useState({});
  const scrollShellRef = useRef(null);
  const scrollMainRef = useRef(null);
  const skinGaugeCanvasRef = useRef(null);
  const concernGaugeCopyCanvasRef = useRef(null);
  const concernGaugeOverlayCanvasRef = useRef(null);
  const concernPreviewCanvasRef = useRef(null);
  const concernGridRef = useRef(null);
  const radarModalTouchStartXRef = useRef(null);
  const concernSelectAnimTimeoutRef = useRef(null);
  const deepDiveAttemptedRef = useRef(false);
  const recommendationsFlowTrackedRef = useRef(false);
  const isMountedRef = useRef(true);
  const aqiDataFromState = effectiveState?.aqiData ?? location.state?.aqiData ?? null;
  const sideCaptures = effectiveState?.sideCaptures || location.state?.sideCaptures || null;
  const sideResultsRaw = skinResultsFromState?.sideResults;
  const sideResults = useMemo(() => (Array.isArray(sideResultsRaw) ? sideResultsRaw : []), [sideResultsRaw]);

  // Persist cartItems and selected concern state so they survive refresh
  useEffect(() => {
    if (!sessionId) return;
    const current = getSkinFlowState(sessionId) || {};
    const fromLocation = location.state && typeof location.state === 'object' ? location.state : {};
    const merged = {
      ...current,
      ...fromLocation,
      sessionId: sessionId,
      cartItems,
      previousSkinData,
      aiConditionDescriptions,
      selectedCondition: selectedConcernName || null,
      imageView: selectedConcernView
    };
    saveSkinFlowState(sessionId, merged);
  }, [sessionId, cartItems, previousSkinData, aiConditionDescriptions, selectedConcernName, selectedConcernView, location.state]);

  // Back to top: show when either shell or main is scrolled (shell scrolls on mobile, main on desktop)
  useEffect(() => {
    const shell = scrollShellRef.current;
    const main = scrollMainRef.current;
    const onScroll = () => {
      const shellTop = shell?.scrollTop ?? 0;
      const mainTop = main?.scrollTop ?? 0;
      setShowBackToTop(shellTop > 300 || mainTop > 300);
    };
    onScroll();
    shell?.addEventListener('scroll', onScroll, { passive: true });
    main?.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      shell?.removeEventListener('scroll', onScroll);
      main?.removeEventListener('scroll', onScroll);
    };
  }, [isLoading, error]);
  const handleScrollToTop = useCallback(() => {
    scrollShellRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    scrollMainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const triggerConcernSelectionAnimation = useCallback((concernName) => {
    if (!concernName) return;
    if (concernSelectAnimTimeoutRef.current) {
      clearTimeout(concernSelectAnimTimeoutRef.current);
      concernSelectAnimTimeoutRef.current = null;
    }
    setAnimatedConcernName(null);
    requestAnimationFrame(() => {
      setAnimatedConcernName(concernName);
      concernSelectAnimTimeoutRef.current = setTimeout(() => {
        setAnimatedConcernName((current) => (current === concernName ? null : current));
        concernSelectAnimTimeoutRef.current = null;
      }, 900);
    });
  }, []);

  const handleSelectConcern = useCallback((concernName) => {
    const canonical = canonicalSkinConditionName(concernName) || concernName;
    setSelectedConcernName(canonical);
    triggerConcernSelectionAnimation(canonical);
    setIsConcernTextExpanded(false);
  }, [triggerConcernSelectionAnimation]);

  const handleToggleConcernTextExpand = useCallback(() => {
    setIsConcernTextExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    return () => {
      if (concernSelectAnimTimeoutRef.current) {
        clearTimeout(concernSelectAnimTimeoutRef.current);
        concernSelectAnimTimeoutRef.current = null;
      }
    };
  }, []);

  const getStoredAqiData = () => {
    if (aqiDataFromState) return aqiDataFromState;
    if (sessionId) {
      try {
        const stored = localStorage.getItem(`skin_aqi_${sessionId}`);
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (e) {
      }
    }
    return null;
  };

  const [aqiData, setAqiData] = useState(getStoredAqiData());
  const [aqiLoading, setAqiLoading] = useState(!getStoredAqiData());
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [routineLoading, setRoutineLoading] = useState(!storedData);
  const [showFallbackSummary, setShowFallbackSummary] = useState(!storedData?.summary);
  const hasGeneratedRef = useRef(!!storedData);
  // final report save tracking removed for skin flow
  const API_BASE_URL = getApiBaseUrl() || '';

  const FALLBACK_SUMMARY = "Based on your skin analysis, we've prepared a personalized routine to help you achieve your skin goals.";

  const formatAiSummaryHtml = useCallback((text) => {
    if (!text || typeof text !== 'string') return '';
    let s = text;
    // Normalize newlines
    s = s.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    // Insert line breaks before section headers and list items when model returns a single paragraph
    s = s
      .replace(/\s*(\*\*\s*1\.\s*Summary\s*\*\*)/gi, '\n\n$1\n')
      .replace(/\s*(\*\*\s*2\.\s*Key Concerns[\s\S]*?\*\*)/gi, '\n\n$1\n')
      .replace(/\s*(\*\*\s*3\.\s*Why this is happening[\s\S]*?\*\*)/gi, '\n\n$1\n')
      .replace(/\s-\s/g, '\n- ')
      .replace(/\s(\d+\.)\s+\*\*/g, '\n$1 **');

    // Minimal markdown bold -> strong
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // Newlines -> <br/>
    const html = sanitizeHtml(s.replace(/\n/g, '<br />'));
    return html;
  }, []);

  // Single fetch for routine page controls (summary/pdf/products + concerns/conditions segmentation).
  useEffect(() => {
    if (!token) {
      setAiGeneratedSummary(null);
      setDownloadPdfTemplate(null);
      setAiMode(null);
      setProductSuggestionEnabled(true);
      setAdvancedGraphicalChartsEnabled(true);
      setProductSuggestLimit(null);
      setOrganizationId(null);
      setShowSkinConcerns(true);
      setShowSkinConditions(true);
      setSegmentationAllowedNames(null);
      setSegmentationAddToCart(true);
      setSegmentationProductLink(true);
      setProductRecommendationSource('database');
      setProductsPageUrl('');
      setProductsPageUrls([]);
      setSegmentationConfigLoaded(true);
      return;
    }

    let cancelled = false;
    setSegmentationConfigLoaded(false);
    fetchWithTimeout(`${SEGMENTATION_API_BASE}/${encodeURIComponent(token)}/segmentation`, { timeoutMs: 20000 })
      .then((res) => {
        if (!res.ok) throw new Error('Segmentation config unavailable');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;

        setAiGeneratedSummary(toYesNoValue(data?.ai_generated_summary));
        setDownloadPdfTemplate(toYesNoValue(data?.download_pdf_template));
        const mode = String(data?.ai_mode || '').trim().toLowerCase();
        setAiMode(mode === 'advanced' || mode === 'basic' ? mode : null);

        const hasProductFlag = Object.prototype.hasOwnProperty.call(data || {}, 'product_suggestion')
          || Object.prototype.hasOwnProperty.call(data || {}, 'product');
        const productEnabled = hasProductFlag
          ? parseFeatureToggle(data?.product_suggestion ?? data?.product)
          : true;
        setProductSuggestionEnabled(productEnabled);

        const recSource =
          data?.product_recommendation_source === 'products_page' ? 'products_page' : 'database';
        const limit = data?.product_suggest_limit;
        const num = typeof limit === 'number' ? limit : parseInt(limit, 10);
        setProductSuggestLimit(
          recSource === 'products_page' || Number.isNaN(num) || num < 1 ? null : num
        );

        const orgId = data?.organization_id;
        setOrganizationId(orgId != null && orgId !== '' ? String(orgId) : null);

        const fields = Array.isArray(data?.fields) ? data.fields : [];
        const segmentationMap = data?.segmentation && typeof data.segmentation === 'object' ? data.segmentation : null;

        let concernsEnabled = segmentationMap?.['Skin Concerns'] != null
          ? parseFeatureToggle(segmentationMap['Skin Concerns'])
          : true;
        let conditionsEnabled = segmentationMap?.['Skin Conditions'] != null
          ? parseFeatureToggle(segmentationMap['Skin Conditions'])
          : true;
        let advancedChartsEnabled = segmentationMap?.['Advanced graphical charts'] != null
          ? parseFeatureToggle(segmentationMap['Advanced graphical charts'])
          : true;

        const allowed = new Set();
        fields.forEach((field) => {
          const label = String(field?.label || '').trim();
          if (label === 'Skin Concerns' && segmentationMap?.['Skin Concerns'] == null) {
            concernsEnabled = parseFeatureToggle(field?.key);
          }
          if (label === 'Skin Conditions' && segmentationMap?.['Skin Conditions'] == null) {
            conditionsEnabled = parseFeatureToggle(field?.key);
          }
          if (label === 'Advanced graphical charts' && segmentationMap?.['Advanced graphical charts'] == null) {
            advancedChartsEnabled = parseFeatureToggle(field?.key);
          }

          if (field?.key !== 'yes' || !Array.isArray(field?.options)) return;
          field.options.forEach((opt) => {
            const normalized = normalizeSegmentationOption(opt);
            if (normalized) allowed.add(normalized);
          });
        });

        setShowSkinConcerns(concernsEnabled);
        setShowSkinConditions(conditionsEnabled);
        setAdvancedGraphicalChartsEnabled(advancedChartsEnabled);
        const productActions = resolveSegmentationProductActions(data);
        setSegmentationAddToCart(productActions.addToCart);
        setSegmentationProductLink(productActions.productLink);
        setProductRecommendationSource(recSource);
        setProductsPageUrl(String(data?.products_page_url || '').trim());
        const urls = Array.isArray(data?.products_page_urls)
          ? data.products_page_urls.map((u) => String(u || '').trim()).filter(Boolean)
          : [];
        setProductsPageUrls(urls.length ? urls : (data?.products_page_url ? [String(data.products_page_url).trim()] : []));
        setSegmentationAllowedNames(allowed.size > 0 ? allowed : (fields.length > 0 ? new Set() : null));
        setSegmentationConfigLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setAiGeneratedSummary(null);
          setDownloadPdfTemplate(null);
          setAiMode(null);
          setProductSuggestionEnabled(true);
          setAdvancedGraphicalChartsEnabled(true);
          setProductSuggestLimit(null);
          setOrganizationId(null);
          setShowSkinConcerns(true);
          setShowSkinConditions(true);
          setSegmentationAllowedNames(null);
          setSegmentationAddToCart(true);
          setSegmentationProductLink(true);
          setSegmentationConfigLoaded(true);
        }
      });
    return () => { cancelled = true; };
  }, [token]);

  // Hide Summary tab only when session config ai_generated_summary === "no"
  const showSummaryAndInsights = SKIP_AI ? false : (aiGeneratedSummary !== 'no');

  // Show PDF button only when session config download_pdf_template === "yes"; hide when "no" or not yet fetched
  const showPdfButton = downloadPdfTemplate === 'yes';

  // Product suggest limit and organization_id are set by segmentation fetch.
  const [productSuggestLimit, setProductSuggestLimit] = useState(null);

  // Restore cart when returning from checkout
  useEffect(() => {
    if (updatedCartFromCheckout && cartItemsFromState) {
      setCartItems(cartItemsFromState);
    }
  }, [cartItemsFromState, updatedCartFromCheckout]);

  // Concerns and conditions are controlled by segmentation payload fetched above.
  const [segmentationAllowedNames, setSegmentationAllowedNames] = useState(null);

  // Helper function to get UV Index color based on value
  const getUvIndexColor = (uvIndex) => {
    if (uvIndex <= 2) return '#00E400'; // Low - Green
    if (uvIndex <= 5) return '#FFFF00'; // Moderate - Yellow
    if (uvIndex <= 7) return '#FF7E00'; // High - Orange
    if (uvIndex <= 10) return '#FF0000'; // Very High - Red
    return '#8F3F97'; // Extreme - Purple
  };

  const getUvDescription = (uvIndex) => {
    if (uvIndex == null || uvIndex === undefined) return 'N/A';
    const n = Number(uvIndex);
    if (n <= 2) return 'Low';
    if (n <= 5) return 'Moderate';
    if (n <= 7) return 'High';
    if (n <= 10) return 'Very High';
    return 'Extreme';
  };

  // Segmentation-only mode: keep AQI loading resolved without network requests.
  useEffect(() => {
    setAqiData(aqiDataFromState && aqiDataFromState.success ? aqiDataFromState : null);
    setAqiLoading(false);
  }, [aqiDataFromState]);

  useEffect(() => {
    setPreviousSkinLoading(false);
  }, []);

  useEffect(() => {
    if (!advancedGraphicalChartsEnabled || !sessionId || previousSkinData != null) return;
    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 4;

    const fetchPrevious = async () => {
      attempt += 1;
      try {
        const res = await getPreviousSkinAnalysis(sessionId, token);
        if (cancelled) return;
        if (res.success) {
          // Debug: log payload arriving to page
          try { console.debug('SkinRoutinePage: fetched previousSkinData', res.previous); } catch (e) {}
          // Only use previous data for 'before' radar when the backend matched by `skin_user_id`.
          // This avoids showing unrelated mobile-based matches.
          const matchedBy = res.previous?.matched_by || null;
          if (matchedBy === 'skin_user_id') {
            setPreviousSkinData(res.previous || null);
          } else {
            // Clear previous if not matched by skin_user_id
            setPreviousSkinData(null);
          }
          return;
        }
      } catch {
      }
      if (!cancelled && attempt < maxAttempts) {
        const delayMs = attempt <= 2 ? 1200 : 2200;
        setTimeout(fetchPrevious, delayMs);
      }
    };

    fetchPrevious();
    return () => { cancelled = true; };
  }, [advancedGraphicalChartsEnabled, sessionId, token, previousSkinData]);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const loadStoredDescriptions = async () => {
      try {
        const params = new URLSearchParams();
        params.set('session_id', sessionId);
        if (token) params.set('app_session_token', token);
        const response = await fetchWithTimeout(`${API_BASE_URL}/api/get-stored-condition-descriptions?${params.toString()}`, {
          method: 'GET',
          timeoutMs: 20000,
        });
        const data = await response.json().catch(() => ({}));
        if (cancelled || !response.ok || !data?.success) return;
        const normalized = normalizeDescriptionMap(data.condition_descriptions || {});
        if (Object.keys(normalized).length > 0) {
          setAiConditionDescriptions((prev) => ({ ...(prev || {}), ...normalized }));
        }
      } catch {
        // non-blocking
      }
    };

    loadStoredDescriptions();
    return () => {
      cancelled = true;
    };
  }, [API_BASE_URL, sessionId, token]);

  // Generate routine content on mount (when token is present, wait for segmentation config controls first).
  useEffect(() => {
    if (SKIP_AI) {
      // Mark generated state so UI and checkout don't wait for AI content.
      hasGeneratedRef.current = true;
      setIsLoading(false);
      setSummaryLoading(false);
      setRoutineLoading(false);
      return;
    }
    if (hasGeneratedRef.current) {
      return;
    }
    if (token && !segmentationConfigLoaded) {
      return;
    }
    // Required order: products first, then deep-dive, then summary/routine.
    if (!productsStageDone || !deepDiveStageDone) {
      return;
    }

    const generateRoutine = async () => {
      if (!skinResultsFromState) {
        setError('Missing required data');
        setIsLoading(false);
        setRoutineLoading(false);
        return;
      }

      if (aqiLoading) {
        return;
      }

      setSummaryLoading(true);
      setRoutineLoading(true);
      setShowFallbackSummary(true);

      try {
        const API_BASE_URL = getApiBaseUrl() || '';

        // Use only detections for conditions visible on skin results page (detection circles)
        const allDetections = skinResultsFromState.detections || [];
        const detectionsForSummary =
          visibleConditionNames != null
            ? allDetections.filter((d) => {
              const name = d.class_name || d.className;
              return name && visibleConditionNames.includes(name);
            })
            : allDetections;

        // Strip masks from skin_conditions to avoid 413 Request Entity Too Large
        const rawConditions = skinResultsFromState.skinConditions || null;
        const leanSkinConditions = rawConditions && typeof rawConditions === 'object'
          ? Object.fromEntries(
            Object.entries(rawConditions).map(([k, v]) => {
              if (!v || typeof v !== 'object') return [k, v];
              const { mask, ...rest } = v;
              return [k, rest];
            })
          )
          : rawConditions;

        const response = await fetchWithTimeout(`${API_BASE_URL}/api/generate-routine`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            detections: detectionsForSummary,
            skin_score: skinResultsFromState.skinScore || null,
            skin_conditions: leanSkinConditions,
            skin_goals: skinGoalDataFromState?.goals || [],
            user_data: {
              ...(userDataFromState || {}),
              session_id: sessionId
            },
            skin_goal_data: skinGoalDataFromState || { goals: [] },
            aqi_data: aqiData && aqiData.success ? {
              aqi: aqiData.aqi,
              category: aqiData.category,
              location: aqiData.location,
              coordinates: aqiData.coordinates,
              latitude: aqiData.latitude,
              longitude: aqiData.longitude,
              uv_index: aqiData.uv_index !== undefined && aqiData.uv_index !== null ? aqiData.uv_index : null,
              uv_level: aqiData.uv_level,
              temperature: aqiData.temperature !== undefined && aqiData.temperature !== null ? aqiData.temperature : null,
              humidity: aqiData.humidity !== undefined && aqiData.humidity !== null ? aqiData.humidity : null,
              wind_speed: aqiData.wind_speed !== undefined && aqiData.wind_speed !== null ? aqiData.wind_speed : null
            } : null,
            session_id: sessionId,
            app_session_token: token,
            ai_generated_summary: token ? (aiGeneratedSummary === 'yes') : true,
            ai_mode: aiMode || undefined,
            ...(typeof productSuggestLimit === 'number' && productSuggestLimit >= 1
              ? { product_suggest_limit: productSuggestLimit }
              : {}),
            captured_image: capturedImageFromState || skinResultsFromState?.markedImage || null,
          }),
          timeoutMs: 60000,
        });

        if (!response.ok) {
          throw new Error('Failed to generate routine');
        }

        const data = await response.json().catch(() => ({}));

        if (data.success) {
          const routineData = {
            summary: data.summary || '',
            routine: data.routine || { morning: [], evening: [] },
            skincare_tips: data.skincare_tips || [],
          };
          setSummary(routineData.summary);
          setShowFallbackSummary(false);
          setSummaryLoading(false);

          // Populate routine after summary so generation appears in sequence.
          setTimeout(() => {
            if (!isMountedRef.current) return;
            setRoutine(routineData.routine);
            setSkincareTips(routineData.skincare_tips);
            setRoutineLoading(false);
            hasGeneratedRef.current = true;
          }, 0);

          if (sessionId) {
            try {
              localStorage.setItem(`skin_routine_${sessionId}`, JSON.stringify(routineData));
            } catch (e) {
            }
          }
        } else {
          throw new Error(data.error || 'Failed to generate routine');
        }
      } catch (err) {
        setError(err.message || 'Failed to generate routine');
        setSummaryLoading(false);
        setRoutineLoading(false);
      } finally {
        setIsLoading(false);
      }
    };

    generateRoutine();
  }, [skinResultsFromState, skinGoalDataFromState, userDataFromState, aqiData, aqiLoading, sessionId, token, aiGeneratedSummary, aiMode, productSuggestLimit, visibleConditionNames, capturedImageFromState, segmentationConfigLoaded, productsStageDone, deepDiveStageDone, SKIP_AI]);

  // Fetch recommended products (get-stored-products first, then recommend-products if needed)
  // Start fetching when we have skin results - don't wait for full routine generation
  useEffect(() => {
    if (SKIP_AI) {
      deepDiveAttemptedRef.current = true;
      setDeepDiveStageDone(true);
      setIsLoadingConditionDescriptions(false);
      // do not return here — allow product recommendation fetch to proceed when AI is skipped
    }
    if (token && !segmentationConfigLoaded) return;
    if (!productSuggestionEnabled) {
      setRecommendations(null);
      setProductsLoading(false);
      setProductsError(null);
      setProductsStageDone(true);
      return;
    }
    if (!sessionId || !skinResultsFromState) return;

    // Sort products by lowest condition score first (most severe concern first).
    // Products whose conditions have the lowest score surface at the top.
    const conditionScoresForSort = skinResultsFromState?.skinScore?.condition_scores || {};
    const sortBySeverity = (products) => {
      if (!products || products.length === 0) return products;
      const getMinScore = (p) => {
        const conds = p.suggested_for_conditions || [];
        if (conds.length === 0) return 70;
        const scores = conds.map(c => {
          const needle = (c || '').toLowerCase().replace(/[\s_-]+/g, '');
          for (const [k, v] of Object.entries(conditionScoresForSort)) {
            if ((k || '').toLowerCase().replace(/[\s_-]+/g, '') === needle) return v ?? 70;
          }
          return 70;
        });
        return Math.min(...scores);
      };
      return [...products].sort((a, b) => getMinScore(a) - getMinScore(b));
    };

    const trackRecommendationsDisplayed = (count) => {
      if (!sessionId || recommendationsFlowTrackedRef.current || !count) return;
      recommendationsFlowTrackedRef.current = true;
      recordSkinFlowStage(
        sessionId,
        'recommendations_viewed',
        token || null,
        { total_count: count, page_name: 'SkinRoutinePage' }
      ).catch(() => {});
    };

    const fetchProducts = async () => {
      setProductsStageDone(false);
      setProductsLoading(true);
      setProductsError(null);
      try {
        const skinScoreForCoverage = skinResultsFromState?.skinScore || {};
        const detectionsForCoverage = skinResultsFromState?.detections || [];
        const mergedScoresForCoverage = buildRecommendationPayloadScores(
          skinScoreForCoverage,
          detectionsForCoverage
        );
        const detectedNamesForCoverage = detectionsForCoverage
          .map((d) => d.class_name || d.className)
          .filter(Boolean);
        const expectedRecommendableConditions = buildRecommendableConditions(
          detectedNamesForCoverage,
          mergedScoresForCoverage,
          detectionsForCoverage
        );

        const storedParams = new URLSearchParams();
        if (token) storedParams.set('app_session_token', token);
        if (organizationId) storedParams.set('organization_id', organizationId);
        if (productRecommendationSource === 'products_page') storedParams.set('refresh_live', '1');
        const storedQuery = storedParams.toString() ? `?${storedParams.toString()}` : '';
        const storedRes = await fetchWithTimeout(
          `${API_BASE_URL}/api/get-stored-products/${sessionId}${storedQuery}`,
          { timeoutMs: 20000 }
        );
        if (storedRes.ok) {
          const storedData = await storedRes.json().catch(() => ({}));
          const cartAdded = storedData?.cart_added;
          if (Array.isArray(cartAdded) && cartAdded.length > 0) {
            setCartItems((prev) => {
              if (prev && Object.keys(prev).length > 0) return prev;
              const restored = {};
              cartAdded.forEach((item, idx) => {
                if (!item || typeof item !== 'object') return;
                const product = item.product;
                if (!product || typeof product !== 'object') return;
                const quantity = Number(item.quantity) || 0;
                if (quantity <= 0) return;
                const productId = product.product_id || product.id || product.sku || `cart_${idx}`;
                restored[productId] = { product, quantity };
              });
              return Object.keys(restored).length > 0 ? restored : prev;
            });
          }
          const flat = flattenRecommendedProducts(storedData.recommended_products);
          const hasStoredProducts = flat.length > 0;
          // products_page: always re-fetch live catalog (stored IDs are analytics-only snapshots)
          if (
            storedData.success
            && hasStoredProducts
            && productRecommendationSource !== 'products_page'
          ) {
            const flatWithSessions = attachRoutineSessions(sortBySeverity(flat));
            setRecommendations({
              ...storedData,
              recommended_products: flatWithSessions,
            });
            trackRecommendationsDisplayed(flatWithSessions.length);
            try {
              const current = getSkinFlowState(sessionId) || {};
              const merged = { ...current, recommended_products: flatWithSessions, sessionId };
              saveSkinFlowState(sessionId, merged);
            } catch (e) {}
            return;
          }
          if (productRecommendationSource === 'products_page' && hasStoredProducts) {
            console.info(
              '[SkinRoutine] products_page: regenerating from live catalog',
              { storedCount: flat.length, expectedConditions: expectedRecommendableConditions.length }
            );
          }
        }
        if (!skinResultsFromState?.skinScore) {
          setProductsLoading(false);
          setProductsStageDone(true);
          return;
        }
        const skinScore = skinResultsFromState.skinScore;
        const detections = skinResultsFromState.detections || [];
        const mergedConditionScores = buildRecommendationPayloadScores(skinScore, detections);
        const detectedConditionsFromDetections = detections
          .map(d => d.class_name || d.className)
          .filter(Boolean);
        const scoreKeyToConditionName = {
          hydration: 'Hydration',
          oxygen: 'Oxygen',
          smoothness: 'Smoothness',
          dullness: 'Dullness',
          uneven_skintone: 'UnevenSkin',
          unevenskintone: 'UnevenSkin',
          'uneven skintone': 'UnevenSkin',
          'uneven skin': 'UnevenSkin',
          even_tone: 'UnevenSkin',
          eventone: 'UnevenSkin'
        };
        const scoreMapsForConditions = [
          mergedConditionScores,
          skinScore.attribute_scores || {},
          skinScore.skin_label_scores || {},
        ];
        const detectedConditionsFromScores = scoreMapsForConditions
          .flatMap((mapObj) => Object.keys(mapObj || {}))
          .map((key) => {
            const normalizedKey = String(key || '').trim().toLowerCase().replace(/[-\s]+/g, '_');
            return scoreKeyToConditionName[normalizedKey] || key;
          })
          .filter(Boolean);
        const detectedConditions = buildRecommendableConditions(
          Array.from(new Set([
            ...detectedConditionsFromDetections,
            ...detectedConditionsFromScores,
          ])),
          mergedConditionScores,
          detections
        );
        const userData = userDataFromState || {};
        const userSkinType = userData.skin_type != null && userData.skin_type !== '' ? userData.skin_type : null;
        const userAge = userData.age != null && userData.age !== '' ? Number(userData.age) : null;
        const body = {
          session_id: sessionId,
          final_skin_score: skinScore.final_skin_score || skinScore.score || 0,
          skin_grade: skinScore.grade || 'Fair',
          condition_scores: mergedConditionScores,
          detected_conditions: detectedConditions,
          detections,
          skin_label_scores: skinScore.skin_label_scores || {},
          attribute_scores: skinScore.attribute_scores || {},
          app_session_token: token,
          product_recommendation_source: productRecommendationSource,
          ...(productRecommendationSource === 'products_page' && (productsPageUrls.length || productsPageUrl)
            ? {
                products_page_url: productsPageUrls[0] || productsPageUrl,
                products_page_urls: productsPageUrls.length ? productsPageUrls : [productsPageUrl],
              }
            : {}),
          ...(organizationId ? { organization_id: organizationId } : {}),
          ...(userSkinType != null ? { user_skin_type: userSkinType } : {}),
          ...(userAge != null ? { user_age: userAge } : {})
        };
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/recommend-products`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          timeoutMs: 30000,
        });
        const data = await res.json().catch(() => ({}));
        if (data.success && data.recommended_products) {
          const flat = attachRoutineSessions(
            sortBySeverity(flattenRecommendedProducts(data.recommended_products))
          );
          setRecommendations({
            ...data,
            recommended_products: flat,
          });
          trackRecommendationsDisplayed(flat.length);
          try {
            const current = getSkinFlowState(sessionId) || {};
            const merged = { ...current, recommended_products: flat, sessionId };
            saveSkinFlowState(sessionId, merged);
          } catch (e) {}
        } else {
          setProductsError(data.error || 'Failed to load recommendations');
        }
      } catch (err) {
        setProductsError(err.message || 'Failed to load recommendations');
      } finally {
        setProductsLoading(false);
        setProductsStageDone(true);
      }
    };
    fetchProducts();
  }, [sessionId, token, organizationId, skinResultsFromState, skinGoalDataFromState, userDataFromState, API_BASE_URL, productSuggestionEnabled, segmentationConfigLoaded, productRecommendationSource, productsPageUrl, productsPageUrls, SKIP_AI]);

  const handleAddToCart = (product, quantity, productId) => {
    const prevQty = cartItems[productId]?.quantity || 0;
    if (prevQty === 0 && quantity > 0 && sessionId) {
      recordSkinFlowStage(
        sessionId,
        'add_to_cart',
        token || null,
        { page_name: 'SkinRoutinePage', quantity }
      ).catch(() => {});
    }
    setCartItems(prev => {
      if (quantity === 0) {
        const next = { ...prev };
        delete next[productId];
        return next;
      }
      return { ...prev, [productId]: { product, quantity } };
    });
  };

  const getTotalCartItems = () => Object.values(cartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const handleCheckout = () => {
    if (isCheckoutModalOpen) return;

    const cartProducts = Object.entries(cartItems)
      .filter(([_, item]) => item.quantity > 0)
      .map(([productId, item]) => ({ ...item, productId }));
    if (cartProducts.length === 0) {
      alert('Your cart is empty. Please add products to cart first.');
      return;
    }

    recordSkinFlowStage(
      sessionId,
      'checkout_started',
      token || null,
      {
        page_name: 'SkinRoutinePage',
        quantity: cartProducts.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
      }
    ).catch(() => {});

    setIsCheckoutModalOpen(true);
  };

  const handleCloseCheckoutModal = () => setIsCheckoutModalOpen(false);

  const handleCartUpdate = (updatedCartItems) => setCartItems(updatedCartItems);

  const handleViewProduct = useCallback((product, productUrl) => {
    const numericProductId = Number(product?.id ?? product?.product_id);
    const productId = Number.isFinite(numericProductId) ? numericProductId : null;
    const baseUrl = (API_BASE_URL || '').replace(/\/$/, '');
    if (sessionId) {
      recordSkinFlowStage(sessionId, 'product_viewed', token || null, {
        page_name: 'SkinRoutinePage',
      }).catch(() => {});
    }
    if (baseUrl && sessionId && productId) {
      fetchWithTimeout(`${baseUrl}/api/record-viewed-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skin_id: sessionId,
          session_id: sessionId,
          product_id: productId,
          ...(token && { app_session_token: token }),
        }),
        timeoutMs: 10000,
      }).catch(() => {});
    }
    if (productUrl) {
      window.open(productUrl, '_blank', 'noopener,noreferrer');
    }
  }, [API_BASE_URL, sessionId, token]);

  // Persist cart to backend whenever cart changes (Add to Cart, +/- quantity, or modal update)
  const saveCartToBackend = useCallback((items) => {
    const baseUrl = (API_BASE_URL || '').replace(/\/$/, '');
    if (!baseUrl || !sessionId) return;
    const cartProducts = Object.entries(items)
      .filter(([, item]) => item && item.quantity > 0)
      .map(([productId, item]) => ({
        id: Number(productId) || item.product?.id || item.product?.product_id,
        quantity: item.quantity,
      }));
    fetchWithTimeout(`${baseUrl}/api/save-cart-products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        skin_id: sessionId,
        session_id: sessionId,
        ...(token && { app_session_token: token }),
        cart_products: cartProducts
      }),
      timeoutMs: 20000,
    }).then((r) => {
      if (!r.ok) console.warn('Could not save cart to backend');
    }).catch(() => { });

    console.info('save-cart-products payload:', { skin_id: sessionId, cart_products: cartProducts });
  }, [API_BASE_URL, sessionId, token]);

  useEffect(() => {
    saveCartToBackend(cartItems);
  }, [cartItems, saveCartToBackend]);

  const handleCloseUspPopup = () => setUspPopup({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });

  const handleOpenProductDetailPopup = (product, recommendationLine = '') => {
    if (!product) return;
    setProductDetailPopup({ open: true, product, recommendationLine: recommendationLine || '' });
  };

  const handleCloseProductDetailPopup = () => {
    setProductDetailPopup({ open: false, product: null, recommendationLine: '' });
  };

  // Do not gate page readiness on AI summary/routine generation for skin flow
  const isPageReady = !isLoading && !productsLoading && !aqiLoading;

  const handleGeneratePdf = async () => {
    return;
  };

  // Final-report saving has been disabled for the skin flow.

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (uspPopup.open && !e.target.closest('.product-usp-popup') && !e.target.closest('.product-usp')) handleCloseUspPopup();
    };
    if (uspPopup.open) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [uspPopup.open]);

  const handleBack = () => {
    const sid = sessionId || getSessionId();

    if (sid) {
      try {
        clearSkinFlowState(sid);
        localStorage.removeItem(`skin_routine_${sid}`);
        localStorage.removeItem(`skin_recommendations_${sid}`);
      } catch (e) {
        // ignore storage cleanup errors
      }
    }

    navigate(pathWithSessionId('/user-info', sid), {
      state: {
        sessionId: sid,
        ...(token && { token })
      }
    });
  };

  const handleHome = () => {
    setShowHomeConfirmPopup(true);
  };

  const handleCancelHomeNavigation = () => {
    setShowHomeConfirmPopup(false);
  };

  const handleConfirmHomeNavigation = () => {
    setShowHomeConfirmPopup(false);
    navigate(sessionLandingPath || '/');
  };

  // Get user info
  const userName = userDataFromState?.name || 'User';
  const skinType = userDataFromState?.skinType || userDataFromState?.skin_type || 'Not specified';
  const gender = userDataFromState?.gender || 'Not specified';
  const userImage = capturedImageFromState || skinResultsFromState?.markedImage || null;
  const profileSubtitle = `${gender} - ${skinType}`;
  const overallSkinScoreRaw = skinResultsFromState?.skinScore?.final_skin_score ?? skinResultsFromState?.skinScore?.score ?? null;
  const overallSkinScore = overallSkinScoreRaw != null && !Number.isNaN(Number(overallSkinScoreRaw))
    ? Math.max(0, Math.min(100, Math.round(Number(overallSkinScoreRaw))))
    : null;
  const analysisDateValue = useMemo(() => {
    const rawDate = effectiveState?.analysisDate
      || effectiveState?.analysis_date
      || skinResultsFromState?.analysis_date
      || skinResultsFromState?.created_at
      || effectiveState?.createdAt
      || null;
    const parsed = rawDate ? new Date(rawDate) : new Date();
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }, [effectiveState, skinResultsFromState]);
  const analysisDateText = useMemo(() => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(analysisDateValue);
  }, [analysisDateValue]);

  const previousOverallSkinScore = useMemo(() => {
    if (!previousSkinData) return null;
    const prevRaw = previousSkinData?.skinScore?.final_skin_score
      ?? previousSkinData?.skinScore?.score
      ?? previousSkinData?.skin_score?.final_skin_score
      ?? previousSkinData?.skin_score?.score
      ?? previousSkinData?.final_skin_score
      ?? previousSkinData?.score
      ?? null;
    if (prevRaw == null || Number.isNaN(Number(prevRaw))) return null;
    return Math.max(0, Math.min(100, Math.round(Number(prevRaw))));
  }, [previousSkinData]);

  const previousAnalysisDateValue = useMemo(() => {
    const rawDate = previousSkinData?.analysis_date
      || previousSkinData?.created_at
      || previousSkinData?.createdAt
      || previousSkinData?.timestamp
      || null;
    if (!rawDate) return null;
    const parsed = new Date(rawDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [previousSkinData]);

  const previousAnalysisDateText = useMemo(() => {
    if (!previousAnalysisDateValue) return '--';
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(previousAnalysisDateValue);
  }, [previousAnalysisDateValue]);

  const radarDaysBetween = useMemo(() => {
    if (!previousAnalysisDateValue || !analysisDateValue) return null;
    const diffMs = Math.abs(analysisDateValue.getTime() - previousAnalysisDateValue.getTime());
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
  }, [analysisDateValue, previousAnalysisDateValue]);

  useEffect(() => {
    if (overallSkinScore == null) return;
    const canvas = skinGaugeCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const cx = canvas.width / 2;
    const cy = 210;
    const radius = 120;
    const thickness = 30;

    const scoreMap = {
      critical: { color: '#ff4757', label: 'Critical' },
      poor: { color: '#ffa502', label: 'Poor' },
      fair: { color: '#eccc68', label: 'Fair' },
      good: { color: '#7bed9f', label: 'Good' },
      excellent: { color: '#2ed573', label: 'Great' },
    };

    const segments = [
      { start: 0.90, end: 1.13, key: 'critical' },
      { start: 1.15, end: 1.37, key: 'poor' },
      { start: 1.39, end: 1.61, key: 'fair' },
      { start: 1.63, end: 1.85, key: 'good' },
      { start: 1.87, end: 2.10, key: 'excellent' },
    ];

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    segments.forEach((seg) => {
      const config = scoreMap[seg.key];

      ctx.beginPath();
      ctx.arc(cx, cy, radius, seg.start * Math.PI, seg.end * Math.PI);
      ctx.strokeStyle = config.color;
      ctx.lineWidth = thickness;
      ctx.lineCap = 'butt';
      ctx.stroke();

      const midAngle = ((seg.start + seg.end) / 2) * Math.PI;
      const tx = cx + (radius + 35) * Math.cos(midAngle);
      const ty = cy + (radius + 35) * Math.sin(midAngle);

      ctx.save();
      ctx.translate(tx, ty);
      ctx.rotate(midAngle + Math.PI / 2);
      ctx.fillStyle = '#636e72';
      ctx.font = '600 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(config.label.toUpperCase(), 0, 0);
      ctx.restore();
    });

    const startRad = 0.9;
    const endRad = 2.1;
    const needleRad = (startRad + (overallSkinScore / 100) * (endRad - startRad)) * Math.PI;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(needleRad);
    ctx.shadowBlur = 10;
    ctx.shadowColor = 'rgba(0,0,0,0.1)';

    ctx.beginPath();
    ctx.moveTo(0, -4);
    ctx.lineTo(radius - 5, 0);
    ctx.lineTo(0, 4);
    ctx.fillStyle = '#2d3436';
    ctx.fill();

    ctx.beginPath();
    ctx.arc(0, 0, 10, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#2d3436';
    ctx.stroke();
    ctx.restore();
  }, [overallSkinScore]);

  // Unified concerns list (no severity/good split)
  const concernsList = useMemo(() => {
    let allItems = [];
    const skinScore = skinResultsFromState?.skinScore;
    const labelScores = skinScore?.skin_label_scores;
    if (labelScores && typeof labelScores === 'object' && Object.keys(labelScores).length > 0) {
      allItems = Object.entries(labelScores)
        .filter(([name]) => {
          if (!showSkinConcerns && isSkinQualityName(name)) return false;
          if (!showSkinConditions && !isSkinQualityName(name)) return false;
          return segmentationAllowedNames === null || segmentationAllowedNames.has(name);
        })
        .map(([rawName, score]) => {
          const class_name = canonicalSkinConditionName(rawName) || rawName;
          const numScore = typeof score === 'number' ? score : parseFloat(score) || 0;
          return {
            class_name,
            score: Math.round(numScore),
            color: CLASS_COLORS_MAP[class_name] || CLASS_COLORS_MAP[rawName] || 'rgb(140, 80, 200)',
            confidence: 1,
            count: 1,
            class_id: null,
            is_condition: isSkinConditionName(class_name),
          };
        })
        .sort((a, b) => a.score - b.score);
    } else {
      const groupedDetections = (skinResultsFromState?.detections || []).reduce((acc, det) => {
        const className = det.class_name || det.className || 'Unknown';
        if (!showSkinConcerns && isSkinQualityName(className)) return acc;
        if (!showSkinConditions && !isSkinQualityName(className)) return acc;
        if (segmentationAllowedNames !== null && !segmentationAllowedNames.has(className)) return acc;
        const confidence = det.confidence || det.confidence_score || 0;
        const score = det.score !== undefined ? det.score : 100;
        if (!acc[className]) {
          acc[className] = { class_name: className, confidences: [], scores: [], count: 0, class_id: det.class_id };
        }
        acc[className].confidences.push(confidence);
        acc[className].scores.push(score);
        acc[className].count += 1;
        return acc;
      }, {});
      const combinedDetections = Object.values(groupedDetections)
        .filter(g => g.class_id !== 8 && g.class_id !== 7 && g.class_name !== 'Unknown' && g.class_name !== 'Mole' && (segmentationAllowedNames === null || segmentationAllowedNames.has(g.class_name)))
        .map(group => {
          const scores = group.scores?.length ? group.scores : [100];
          const combinedScore = Math.min(...scores);
          const avgConfidence = group.confidences.reduce((s, c) => s + c, 0) / group.confidences.length;
          let color = getColorByClassId(group.class_id);
          if (!BGR_COLORS_BY_ID[group.class_id]) color = CLASS_COLORS_MAP[group.class_name] || 'rgb(140, 80, 200)';
          return {
            class_name: group.class_name,
            score: Math.round(combinedScore),
            confidence: avgConfidence,
            count: group.count,
            color,
            class_id: group.class_id
          };
        });
      const conditionMapping = { 'hydration': 'Hydration', 'oxygen': 'Oxygen', 'smoothness': 'Smoothness', 'dullness': 'Dullness', 'uneven_skintone': 'UnevenSkin' };
      const conditionItems = [];
      const skinConditions = skinResultsFromState?.skinConditions || {};
      Object.entries(conditionMapping).forEach(([key, displayName]) => {
        if (!showSkinConcerns) return;
        if (segmentationAllowedNames !== null && !segmentationAllowedNames.has(displayName)) return;
        if (skinConditions[key]?.score !== undefined) {
          conditionItems.push({
            class_name: displayName,
            score: Math.round(skinConditions[key].score),
            confidence: 1,
            count: 1,
            color: CLASS_COLORS_MAP[displayName] || 'rgb(140, 80, 200)',
            class_id: null,
            is_condition: true
          });
        }
      });
      allItems = [...combinedDetections, ...conditionItems].sort((a, b) => a.score - b.score);
    }
    return allItems;
  }, [skinResultsFromState, segmentationAllowedNames, showSkinConcerns, showSkinConditions]);

  const deepDiveConditionNames = useMemo(() => {
    return concernsList
      .filter((item) => Number(item?.score || 0) < 75)
      .map((item) => item.class_name)
      .filter(Boolean);
  }, [concernsList]);

  const concernRadarItems = useMemo(() => {
    if (!advancedGraphicalChartsEnabled) return [];
    return [...concernsList]
      .sort((a, b) => a.score - b.score)
      .map((item) => ({
        ...item,
        label: getConditionDisplayName(item.class_name),
        intensity: Math.max(5, Math.min(100, Number(item.score || 0))),
      }));
  }, [concernsList, advancedGraphicalChartsEnabled]);

  const radarModel = useMemo(() => {
    if (!advancedGraphicalChartsEnabled || !concernRadarItems.length) return null;
    const size = 380;
    const cx = size / 2;
    const cy = size / 2;
    const radius = 108;
    const axis = concernRadarItems.map((item, idx) => {
      const angle = ((Math.PI * 2 * idx) / concernRadarItems.length) - Math.PI / 2;
      const dataRadius = (item.intensity / 100) * radius;
      const pointX = cx + Math.cos(angle) * dataRadius;
      const pointY = cy + Math.sin(angle) * dataRadius;
      const cos = Math.cos(angle);
      const labelAnchor = cos > 0.35 ? 'start' : cos < -0.35 ? 'end' : 'middle';
      const labelOffsetX = cos > 0.35 ? 3 : cos < -0.35 ? -3 : 0;
      const labelX = cx + Math.cos(angle) * (radius + 14);
      const labelY = cy + Math.sin(angle) * (radius + 14);
      return {
        ...item,
        angle,
        pointX,
        pointY,
        labelX,
        labelY,
        labelAnchor,
        labelOffsetX,
      };
    });
    const polygon = axis.map((a) => `${a.pointX},${a.pointY}`).join(' ');
    return { size, cx, cy, radius, axis, polygon, levels: [20, 40, 60, 80, 100] };
  }, [concernRadarItems, advancedGraphicalChartsEnabled]);

  const previousConcernRadarItems = useMemo(() => {
    if (!advancedGraphicalChartsEnabled) return [];
    const prevScoreObj = previousSkinData?.skinScore ?? previousSkinData?.skin_score;
    const prevLabelScores = prevScoreObj?.skin_label_scores ?? previousSkinData?.skin_label_scores ?? null;
    if (!prevLabelScores || typeof prevLabelScores !== 'object') return [];
    return Object.entries(prevLabelScores)
      .map(([name, score]) => {
        let raw = score;
        if (raw && typeof raw === 'object') {
          raw = raw.score ?? raw.value ?? raw.percentage ?? raw.percent ?? 0;
        }
        const numeric = typeof raw === 'number' ? raw : parseFloat(raw) || 0;
        return {
          class_name: String(name || ''),
          score: Math.round(numeric),
          color: CLASS_COLORS_MAP[String(name || '')] || CLASS_COLORS_MAP[getConditionDisplayName(String(name || ''))] || 'rgb(15, 118, 110)',
        };
      })
      .filter((item) => item.class_name)
      .sort((a, b) => a.score - b.score)
      .map((item) => ({
        ...item,
        label: getConditionDisplayName(item.class_name),
        intensity: Math.max(5, Math.min(100, Number(item.score || 0))),
      }));
  }, [previousSkinData, advancedGraphicalChartsEnabled]);

  const previousRadarModel = useMemo(() => {
    if (!advancedGraphicalChartsEnabled || !previousConcernRadarItems.length) return null;
    const size = 380;
    const cx = size / 2;
    const cy = size / 2;
    const radius = 108;
    const axis = previousConcernRadarItems.map((item, idx) => {
      const angle = ((Math.PI * 2 * idx) / previousConcernRadarItems.length) - Math.PI / 2;
      const dataRadius = (item.intensity / 100) * radius;
      const pointX = cx + Math.cos(angle) * dataRadius;
      const pointY = cy + Math.sin(angle) * dataRadius;
      const cos = Math.cos(angle);
      const labelAnchor = cos > 0.35 ? 'start' : cos < -0.35 ? 'end' : 'middle';
      const labelOffsetX = cos > 0.35 ? 3 : cos < -0.35 ? -3 : 0;
      const labelX = cx + Math.cos(angle) * (radius + 14);
      const labelY = cy + Math.sin(angle) * (radius + 14);
      return {
        ...item,
        angle,
        pointX,
        pointY,
        labelX,
        labelY,
        labelAnchor,
        labelOffsetX,
      };
    });
    const polygon = axis.map((a) => `${a.pointX},${a.pointY}`).join(' ');
    return { size, cx, cy, radius, axis, polygon, levels: [20, 40, 60, 80, 100] };
  }, [previousConcernRadarItems, advancedGraphicalChartsEnabled]);

  const radarComparisonModel = useMemo(() => {
    if (!advancedGraphicalChartsEnabled || !previousConcernRadarItems.length || !concernRadarItems.length) return null;
    const size = 380;
    const cx = size / 2;
    const cy = size / 2;
    const radius = 108;

    const afterMap = new Map(concernRadarItems.map((item) => [normalizeConditionName(item.class_name), item]));
    const beforeMap = new Map(previousConcernRadarItems.map((item) => [normalizeConditionName(item.class_name), item]));
    const orderedKeys = [
      ...Array.from(afterMap.keys()),
      ...Array.from(beforeMap.keys()).filter((key) => !afterMap.has(key)),
    ];

    const axis = orderedKeys.map((key, idx) => {
      const afterItem = afterMap.get(key) || null;
      const beforeItem = beforeMap.get(key) || null;
      const angle = ((Math.PI * 2 * idx) / orderedKeys.length) - Math.PI / 2;
      const afterIntensity = afterItem ? Math.max(5, Math.min(100, Number(afterItem.score || 0))) : 0;
      const beforeIntensity = beforeItem ? Math.max(5, Math.min(100, Number(beforeItem.score || 0))) : 0;
      const afterRadius = (afterIntensity / 100) * radius;
      const beforeRadius = (beforeIntensity / 100) * radius;
      const cos = Math.cos(angle);
      const labelAnchor = cos > 0.35 ? 'start' : cos < -0.35 ? 'end' : 'middle';
      const labelOffsetX = cos > 0.35 ? 3 : cos < -0.35 ? -3 : 0;
      const beforeScore = beforeItem ? Math.round(Number(beforeItem.score || 0)) : null;
      const afterScore = afterItem ? Math.round(Number(afterItem.score || 0)) : null;
      const delta = Number.isFinite(beforeScore) && Number.isFinite(afterScore)
        ? Math.round((afterScore - beforeScore) * 10) / 10
        : null;
      const changeKind = !beforeItem && afterItem
        ? 'new'
        : beforeItem && !afterItem
          ? 'missing'
          : Number.isFinite(delta)
            ? delta >= 3
              ? 'improved'
              : delta <= -3
                ? 'declined'
                : 'stable'
            : 'stable';

      const labelDistance = 18;

      return {
        key,
        label: getConditionDisplayName(afterItem?.class_name || beforeItem?.class_name || key),
        angle,
        hasAfter: Boolean(afterItem),
        hasBefore: Boolean(beforeItem),
        afterScore,
        beforeScore,
        delta,
        changeKind,
        afterPointX: cx + Math.cos(angle) * afterRadius,
        afterPointY: cy + Math.sin(angle) * afterRadius,
        beforePointX: cx + Math.cos(angle) * beforeRadius,
        beforePointY: cy + Math.sin(angle) * beforeRadius,
        labelX: cx + Math.cos(angle) * (radius + labelDistance),
        labelY: cy + Math.sin(angle) * (radius + labelDistance),
        labelAnchor,
        labelOffsetX,
      };
    });

    return {
      size,
      cx,
      cy,
      radius,
      levels: [20, 40, 60, 80, 100],
      axis,
      afterPolygon: axis.map((a) => `${a.afterPointX},${a.afterPointY}`).join(' '),
      beforePolygon: axis.map((a) => `${a.beforePointX},${a.beforePointY}`).join(' '),
    };
  }, [concernRadarItems, previousConcernRadarItems, advancedGraphicalChartsEnabled]);

  const showRadarAnalysisSection = Boolean(
    advancedGraphicalChartsEnabled
    && (!token || segmentationConfigLoaded)
    && ENABLE_RADAR_CHART
  );

  const hasBeforeAfterRadar = Boolean(radarComparisonModel && previousRadarModel && radarModel);
  const canShowBeforeAfterRadar = Boolean(advancedGraphicalChartsEnabled && hasBeforeAfterRadar);
  const isAfterComparisonView = canShowBeforeAfterRadar && radarComparisonTab === 'after';
  const activeRadarModel = canShowBeforeAfterRadar
    ? (radarComparisonTab === 'before' ? previousRadarModel : radarComparisonModel)
    : radarModel;
  useEffect(() => {
    try {
      console.debug('Radar debug: previousConcernRadarItems', previousConcernRadarItems);
      console.debug('Radar debug: previousRadarModel', previousRadarModel);
      console.debug('Radar debug: radarComparisonModel', radarComparisonModel);
      console.debug('Radar debug: hasBeforeAfterRadar', hasBeforeAfterRadar, 'canShowBeforeAfterRadar', canShowBeforeAfterRadar);
    } catch (e) {
      // ignore
    }
  }, [previousConcernRadarItems, previousRadarModel, radarComparisonModel, hasBeforeAfterRadar, canShowBeforeAfterRadar]);
  const activeRadarScore = canShowBeforeAfterRadar && radarComparisonTab === 'before' ? previousOverallSkinScore : overallSkinScore;
  const activeRadarDate = canShowBeforeAfterRadar && radarComparisonTab === 'before' ? previousAnalysisDateText : analysisDateText;

  const activeRadarConcernDetails = useMemo(() => {
    if (!activeRadarModel?.axis?.length) return [];
    if (isAfterComparisonView) {
      return activeRadarModel.axis.map((item, idx) => ({
        idx,
        label: item.label,
        beforeScore: item.beforeScore,
        afterScore: item.afterScore,
        delta: item.delta,
        changeKind: item.changeKind || 'stable',
      }));
    }
    return activeRadarModel.axis.map((item, idx) => ({
      idx,
      label: item.label,
      beforeScore: canShowBeforeAfterRadar ? item.score : null,
      afterScore: canShowBeforeAfterRadar ? null : item.score,
      delta: null,
      changeKind: 'stable',
    }));
  }, [activeRadarModel, canShowBeforeAfterRadar, isAfterComparisonView]);

  const activeRadarConcernDetail = useMemo(() => {
    if (!activeRadarConcernDetails.length) return null;
    const safeIndex = Math.max(0, Math.min(radarConcernModal.index, activeRadarConcernDetails.length - 1));
    return activeRadarConcernDetails[safeIndex] || null;
  }, [activeRadarConcernDetails, radarConcernModal.index]);

  const openRadarConcernModal = useCallback((index) => {
    if (!activeRadarConcernDetails.length) return;
    const safeIndex = Math.max(0, Math.min(index, activeRadarConcernDetails.length - 1));
    setRadarConcernModal({ open: true, index: safeIndex });
  }, [activeRadarConcernDetails]);

  const closeRadarConcernModal = useCallback(() => {
    setRadarConcernModal((prev) => ({ ...prev, open: false }));
  }, []);

  const stepRadarConcernModal = useCallback((step) => {
    setRadarConcernModal((prev) => {
      const total = activeRadarConcernDetails.length;
      if (!total) return prev;
      const nextIndex = (prev.index + step + total) % total;
      return { ...prev, index: nextIndex };
    });
  }, [activeRadarConcernDetails.length]);

  const deepDiveLines = useMemo(() => {
    return deepDiveConditionNames
      .map((name) => {
        const text = aiConditionDescriptions?.[name] || aiConditionDescriptions?.[getConditionDisplayName(name)] || '';
        if (!text || !String(text).trim()) return null;
        return String(text).trim();
      })
      .filter(Boolean);
  }, [deepDiveConditionNames, aiConditionDescriptions]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (token && !segmentationConfigLoaded) return;
    if (!advancedGraphicalChartsEnabled) {
      deepDiveAttemptedRef.current = true;
      setDeepDiveStageDone(true);
      setIsLoadingConditionDescriptions(false);
      return;
    }
    if (!sessionId || !skinResultsFromState || deepDiveAttemptedRef.current) return;
    if (!productsStageDone) return;
    if (deepDiveConditionNames.length === 0) return;

    const missing = deepDiveConditionNames.filter((name) => {
      const displayName = getConditionDisplayName(name);
      return !aiConditionDescriptions?.[name] && !aiConditionDescriptions?.[displayName];
    });
    if (missing.length === 0) {
      deepDiveAttemptedRef.current = true;
      setDeepDiveStageDone(true);
      return;
    }

    deepDiveAttemptedRef.current = true;

    const generateDeepDive = async () => {
      setIsLoadingConditionDescriptions(true);
      try {
        const allDetections = skinResultsFromState?.detections || [];
        const detectionsForDeepDive =
          visibleConditionNames != null
            ? allDetections.filter((d) => {
              const name = d.class_name || d.className;
              return name && visibleConditionNames.includes(name);
            })
            : allDetections;
        const rawConditions = skinResultsFromState?.skinConditions || null;
        const leanSkinConditions = rawConditions && typeof rawConditions === 'object'
          ? Object.fromEntries(
            Object.entries(rawConditions).map(([k, v]) => {
              if (!v || typeof v !== 'object') return [k, v];
              const { mask, ...rest } = v;
              return [k, rest];
            })
          )
          : rawConditions;

        const response = await fetchWithTimeout(`${API_BASE_URL}/api/generate-analysis-deep-dive`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            detections: detectionsForDeepDive,
            skin_score: skinResultsFromState?.skinScore || null,
            previous_skin_data: previousSkinData,
            skin_conditions: leanSkinConditions,
            user_data: {
              ...(userDataFromState || {}),
              session_id: sessionId,
            },
            aqi_data: aqiData && aqiData.success ? {
              aqi: aqiData.aqi,
              category: aqiData.category,
              location: aqiData.location,
              latitude: aqiData.latitude,
              longitude: aqiData.longitude,
              uv_index: aqiData.uv_index !== undefined && aqiData.uv_index !== null ? aqiData.uv_index : null,
              uv_level: aqiData.uv_level,
              temperature: aqiData.temperature !== undefined && aqiData.temperature !== null ? aqiData.temperature : null,
              humidity: aqiData.humidity !== undefined && aqiData.humidity !== null ? aqiData.humidity : null,
              wind_speed: aqiData.wind_speed !== undefined && aqiData.wind_speed !== null ? aqiData.wind_speed : null,
            } : null,
            session_id: sessionId,
            app_session_token: token,
            ai_mode: aiMode || undefined,
            requested_conditions: missing,
          }),
          timeoutMs: 60000,
        });
        const data = await response.json().catch(() => ({}));
        if (!isMountedRef.current || !response.ok || !data?.success || !data?.deep_dive || typeof data.deep_dive !== 'object') {
          return;
        }
        const normalized = normalizeDescriptionMap(data.deep_dive);
        if (Object.keys(normalized).length === 0) return;
        setAiConditionDescriptions((prev) => ({ ...(prev || {}), ...normalized }));
      } catch {
        // non-blocking
      } finally {
        if (isMountedRef.current) setIsLoadingConditionDescriptions(false);
        if (isMountedRef.current) setDeepDiveStageDone(true);
      }
    };

    generateDeepDive();
  }, [API_BASE_URL, aqiData, aiConditionDescriptions, aiMode, advancedGraphicalChartsEnabled, deepDiveConditionNames, previousSkinData, sessionId, skinResultsFromState, token, userDataFromState, visibleConditionNames, productsStageDone, segmentationConfigLoaded]);

  useEffect(() => {
    if (token && !segmentationConfigLoaded) return;
    if (!sessionId || !skinResultsFromState) return;
    if (!productsStageDone) return;
    if (deepDiveConditionNames.length > 0) return;
    setDeepDiveStageDone(true);
  }, [token, segmentationConfigLoaded, sessionId, skinResultsFromState, productsStageDone, deepDiveConditionNames.length]);

  const detailedConcernItems = useMemo(() => {
    return [...concernsList]
      .sort((a, b) => a.score - b.score)
      .map((item) => ({
        ...item,
        label: getConditionDisplayName(item.class_name),
      }));
  }, [concernsList]);

  const selectedConcernItem = useMemo(() => {
    if (!selectedConcernName) return null;
    return detailedConcernItems.find((item) => item.class_name === selectedConcernName) || null;
  }, [detailedConcernItems, selectedConcernName]);

  useEffect(() => {
    const score = selectedConcernItem?.score;
    if (score == null) return;

    const drawConcernGauge = (canvas) => {
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const cx = 200;
      const cy = 200;
      const outerRadius = 150;
      const innerRadius = 115;
      const totalSegments = 20;
      const gap = 0.04;

      const getSegmentColor = (segmentValue) => {
        if (segmentValue >= 0 && segmentValue <= 39) return '#ff4757';
        if (segmentValue >= 40 && segmentValue <= 59) return '#ffa502';
        if (segmentValue >= 60 && segmentValue <= 74) return '#eccc68';
        if (segmentValue >= 75 && segmentValue <= 89) return '#7bed9f';
        return '#2ed573';
      };

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < totalSegments; i += 1) {
        const segmentValue = (i / totalSegments) * 100;
        const startAngle = Math.PI + (i * (Math.PI / totalSegments)) + gap;
        const endAngle = Math.PI + ((i + 1) * (Math.PI / totalSegments)) - gap;

        ctx.beginPath();
        ctx.arc(cx, cy, outerRadius, startAngle, endAngle);
        ctx.arc(cx, cy, innerRadius, endAngle, startAngle, true);
        ctx.closePath();
        ctx.fillStyle = segmentValue <= score ? getSegmentColor(segmentValue) : '#f1f2f6';
        ctx.fill();
      }

      const needleAngle = Math.PI + (score / 100) * Math.PI;

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(needleAngle);

      ctx.beginPath();
      ctx.moveTo(0, -4);
      ctx.lineTo(innerRadius - 10, 0);
      ctx.lineTo(0, 4);
      ctx.fillStyle = '#2d3436';
      ctx.fill();

      ctx.beginPath();
      ctx.arc(0, 0, 12, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.strokeStyle = '#2d3436';
      ctx.lineWidth = 3;
      ctx.stroke();

      ctx.restore();
    };

    drawConcernGauge(concernGaugeCopyCanvasRef.current);
    drawConcernGauge(concernGaugeOverlayCanvasRef.current);
  }, [selectedConcernItem]);

  const selectedConcernDescription = useMemo(() => {
    if (!selectedConcernItem) return '';
    const fallback = getConditionDescription(selectedConcernItem.class_name);
    return typeof fallback === 'string' ? fallback : '';
  }, [selectedConcernItem]);

  const normalizeConcernToken = useCallback((value) => {
    const raw = String(value || '').toLowerCase().trim();
    if (!raw) return '';
    let cleaned = raw
      .replace(/\([^)]*\)/g, ' ')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (cleaned.endsWith('ies') && cleaned.length > 3) cleaned = `${cleaned.slice(0, -3)}y`;
    else if (cleaned.endsWith('es') && cleaned.length > 3) cleaned = cleaned.slice(0, -2);
    else if (cleaned.endsWith('s') && cleaned.length > 2) cleaned = cleaned.slice(0, -1);
    const aliases = {
      'uneven skin': 'uneven skintone',
      'uneven tone': 'uneven skintone',
      'even tone': 'uneven skintone',
      'uneven texture': 'uneven skintone',
      'texture': 'smoothness'
    };
    if (cleaned.includes('uneven skin') && (cleaned.includes('tone') || cleaned.includes('texture'))) {
      cleaned = 'uneven skintone';
    }
    cleaned = aliases[cleaned] || cleaned;
    return cleaned;
  }, []);

  const selectedConcernSeverityInfo = useMemo(() => {
    const score = Number(selectedConcernItem?.score || 0);
    if (score <= 39) {
      return {
        level: 'High Priority',
        meaning: 'This concern is currently in a high-impact range and should be addressed consistently to avoid compounding skin stress.',
      };
    }
    if (score <= 59) {
      return {
        level: 'Needs Attention',
        meaning: 'This concern is active and visible. A stable routine and daily protection can improve progression over time.',
      };
    }
    if (score <= 74) {
      return {
        level: 'Moderate',
        meaning: 'This concern is manageable but can shift quickly with weather, hydration, or routine inconsistency.',
      };
    }
    if (score <= 89) {
      return {
        level: 'Mild',
        meaning: 'This area is mostly stable. Keep maintenance-focused care to preserve current skin quality.',
      };
    }
    return {
      level: 'Healthy Zone',
      meaning: 'This concern is well-controlled right now. Continue your routine to hold these results.',
    };
  }, [selectedConcernItem]);

  const selectedConcernWhyText = useMemo(() => {
    if (!selectedConcernItem) return 'Detailed drivers are not available yet for this concern.';
    const directText = aiConditionDescriptions?.[selectedConcernItem.class_name];
    const displayText = aiConditionDescriptions?.[selectedConcernItem.label];
    const source = directText || displayText || selectedConcernDescription;
    if (!source || typeof source !== 'string') {
      return 'Detailed drivers are not available yet for this concern.';
    }
    const cleaned = source.replace(new RegExp(`^${selectedConcernItem.label}:\\s*`, 'i'), '').trim();
    return cleaned || 'Detailed drivers are not available yet for this concern.';
  }, [aiConditionDescriptions, selectedConcernDescription, selectedConcernItem]);

  const selectedConcernActionText = useMemo(() => {
    const concernName = selectedConcernItem?.class_name || selectedConcernItem?.label || '';
    const concernKey = normalizeConcernToken(concernName);
    const morningProducts = recommendations?.recommended_products?.morning || [];
    const eveningProducts = recommendations?.recommended_products?.evening || [];
    const allProducts = dedupeProductsById([...morningProducts, ...eveningProducts]);

    const concernAliasMap = {
      'under eye': ['eye bag', 'eye bags', 'dark circle', 'dark circles', 'under eye', 'under-eye'],
      'eye bag': ['eye bag', 'eye bags', 'under eye', 'under-eye', 'dark circle', 'dark circles'],
      'eye bags': ['eye bag', 'eye bags', 'under eye', 'under-eye', 'dark circle', 'dark circles'],
      'dark circle': ['dark circle', 'dark circles', 'under eye', 'under-eye', 'eye bag', 'eye bags'],
      'dark circles': ['dark circle', 'dark circles', 'under eye', 'under-eye', 'eye bag', 'eye bags'],
      'uneven skintone': ['uneven skintone', 'uneven skin', 'even tone', 'uneven tone'],
      'hydration': ['hydration', 'dry skin', 'moisture'],
      'oxygen': ['oxygen', 'dullness', 'radiance'],
      'smoothness': ['smoothness', 'roughness', 'texture'],
      'dullness': ['dullness', 'radiance', 'glow']
    };
    const concernAliases = Array.from(new Set([
      concernKey,
      ...(concernAliasMap[concernKey] || [])
    ].map((alias) => normalizeConcernToken(alias)).filter(Boolean)));

    const matchesConcern = (product) => {
      const treated = Array.isArray(product?.concerns_treated) ? product.concerns_treated : [];
      const suggested = Array.isArray(product?.suggested_for_conditions) ? product.suggested_for_conditions : [];
      const wording = String(product?.suggested_for_wording || product?.why_recommended || '').toLowerCase();
      // Normalize and alias all product concern strings
      const merged = [...treated, ...suggested]
        .map((item) => normalizeConcernToken(item))
        .map((item) => {
          // Robust mapping for all known database variants
          const lower = item.toLowerCase();
          if (lower.includes('smoothness') || lower.includes('texture') || lower.includes('lack of smoothness')) return 'smoothness';
          if (lower.includes('uneven skin') || lower.includes('uneven tone') || lower.includes('even tone') || lower.includes('uneven texture')) return 'uneven skintone';
          if (lower.includes('oxygen')) return 'oxygen';
          if (lower.includes('hydration')) return 'hydration';
          if (lower.includes('dullness')) return 'dullness';
          if (lower.includes('pigmentation')) return 'pigmentation';
          if (lower.includes('wrinkle')) return 'wrinkles';
          if (lower.includes('acne')) return 'acne';
          if (lower.includes('dark circle')) return 'dark circles';
          if (lower.includes('open pore')) return 'open pores';
          if (lower.includes('clogged pore')) return 'clogged pores';
          return item;
        })
        .filter(Boolean);
      return merged.some((item) => concernAliases.some((alias) => item === alias || item.includes(alias) || alias.includes(item)))
        || concernAliases.some((alias) => alias && wording.includes(alias));
    };

    const matchedProducts = allProducts.filter(matchesConcern);
    const topMatched = matchedProducts.slice(0, 2).map((p) => p.product_name).filter(Boolean);
    if (topMatched.length > 0) {
      return `Products matched for ${selectedConcernItem?.label || concernName}. Tap Open to view full details.`;
    }

    return '';
  }, [normalizeConcernToken, recommendations, selectedConcernItem]);

  const selectedConcernActionProducts = useMemo(() => {
    const concernName = selectedConcernItem?.class_name || selectedConcernItem?.label || '';
    const concernKey = normalizeConcernToken(concernName);
    const morningProducts = recommendations?.recommended_products?.morning || [];
    const eveningProducts = recommendations?.recommended_products?.evening || [];
    const allProducts = dedupeProductsById([...morningProducts, ...eveningProducts]);

    const concernAliasMap = {
      'under eye': ['eye bag', 'eye bags', 'dark circle', 'dark circles', 'under eye', 'under-eye'],
      'eye bag': ['eye bag', 'eye bags', 'under eye', 'under-eye', 'dark circle', 'dark circles'],
      'eye bags': ['eye bag', 'eye bags', 'under eye', 'under-eye', 'dark circle', 'dark circles'],
      'dark circle': ['dark circle', 'dark circles', 'under eye', 'under-eye', 'eye bag', 'eye bags'],
      'dark circles': ['dark circle', 'dark circles', 'under eye', 'under-eye', 'eye bag', 'eye bags'],
      'uneven skintone': ['uneven skintone', 'uneven skin', 'even tone', 'uneven tone'],
      'hydration': ['hydration', 'dry skin', 'moisture'],
      'oxygen': ['oxygen', 'dullness', 'radiance'],
      'smoothness': ['smoothness', 'roughness', 'texture'],
      'dullness': ['dullness', 'radiance', 'glow']
    };
    const concernAliases = Array.from(new Set([
      concernKey,
      ...(concernAliasMap[concernKey] || [])
    ].map((alias) => normalizeConcernToken(alias)).filter(Boolean)));

    const matchesConcern = (product) => {
      const treated = Array.isArray(product?.concerns_treated) ? product.concerns_treated : [];
      const suggested = Array.isArray(product?.suggested_for_conditions) ? product.suggested_for_conditions : [];
      const wording = String(product?.suggested_for_wording || product?.why_recommended || '').toLowerCase();
      const merged = [...treated, ...suggested]
        .map((item) => normalizeConcernToken(item))
        .filter(Boolean);
      return merged.some((item) => concernAliases.some((alias) => item === alias || item.includes(alias) || alias.includes(item)))
        || concernAliases.some((alias) => alias && wording.includes(alias));
    };

    const matched = allProducts.filter(matchesConcern);
    return matched;
  }, [normalizeConcernToken, recommendations, selectedConcernItem]);

  const selectedConcernSeverityGradientStyle = useMemo(
    () => buildSeverityBarGradientStyleVars(selectedConcernName),
    [selectedConcernName],
  );

  const selectedSeverityBarLabels = useMemo(
    () => getSeverityBarLabels(selectedConcernName),
    [selectedConcernName],
  );

  const availableConcernViews = useMemo(() => {
    const views = ['front'];
    const getSideImage = (item) => (
      item?.captured_image
      || item?.capturedImage
      || item?.pipeline?.captured_image
      || item?.raw_image
      || item?.marked_image
      || null
    );
    const hasLeft = Boolean(
      sideCaptures?.left || sideResults.some((item) => item?.key === 'left' && getSideImage(item))
    );
    const hasRight = Boolean(
      sideCaptures?.right || sideResults.some((item) => item?.key === 'right' && getSideImage(item))
    );
    if (hasLeft) views.push('left');
    if (hasRight) views.push('right');
    return views;
  }, [sideCaptures, sideResults]);

  const hasMultiAnglePreview = availableConcernViews.length > 1;
  const isSelectedSkinConditionConcern = Boolean(
    selectedConcernName && isSkinConditionName(selectedConcernName),
  );
  const showConcernAngleNavigation = hasMultiAnglePreview && !isSelectedSkinConditionConcern;

  const moveConcernPreviewBy = useCallback((direction) => {
    if (!showConcernAngleNavigation) return;
    const idx = availableConcernViews.indexOf(selectedConcernView);
    const currentIndex = idx >= 0 ? idx : 0;
    const nextIndex = (currentIndex + direction + availableConcernViews.length) % availableConcernViews.length;
    setSelectedConcernView(availableConcernViews[nextIndex]);
  }, [availableConcernViews, showConcernAngleNavigation, selectedConcernView]);

  const drawConcernPreviewDetections = useCallback((ctx, conditionName, detectionsToUse, imgWidth) => {
    if (!ctx || !conditionName || !Array.isArray(detectionsToUse) || detectionsToUse.length === 0) return;
    // Cluster overlapping detections (IoU >= 0.75)
    const iou = (a, b) => {
      const bbA = a?.bbox || [];
      const bbB = b?.bbox || [];
      if (bbA.length !== 4 || bbB.length !== 4) return 0;
      const [ax1, ay1, ax2, ay2] = bbA;
      const [bx1, by1, bx2, by2] = bbB;
      const ix1 = Math.max(ax1, bx1);
      const iy1 = Math.max(ay1, by1);
      const ix2 = Math.min(ax2, bx2);
      const iy2 = Math.min(ay2, by2);
      if (ix2 <= ix1 || iy2 <= iy1) return 0;
      const inter = (ix2 - ix1) * (iy2 - iy1);
      const areaA = Math.max(1, (ax2 - ax1) * (ay2 - ay1));
      const areaB = Math.max(1, (bx2 - bx1) * (by2 - by1));
      return inter / (areaA + areaB - inter);
    };
    const drawListRaw = getDetectionsToDraw(conditionName, detectionsToUse, imgWidth);
    const clusters = [];
    const used = new Array(drawListRaw.length).fill(false);
    for (let i = 0; i < drawListRaw.length; i += 1) {
      if (used[i]) continue;
      const base = drawListRaw[i];
      const cluster = [base];
      used[i] = true;
      for (let j = i + 1; j < drawListRaw.length; j += 1) {
        if (used[j]) continue;
        if (iou(base, drawListRaw[j]) >= 0.75) {
          used[j] = true;
          cluster.push(drawListRaw[j]);
        }
      }
      clusters.push(cluster);
    }
    const drawList = clusters.map((list) => {
      const best = list.reduce((acc, d) => ((d?.confidence ?? 0) > (acc?.confidence ?? 0) ? d : acc), list[0]);
      return { det: best, stackedCount: list.length };
    });
    drawList.forEach(({ det, stackedCount }) => {
      if (det?.ui_hidden === true) return;
      const bbox = det?.bbox || [];
      if (bbox.length !== 4) return;
      const [x1, y1, x2, y2] = bbox;
      const classId = det.class_id;
      const color = getColorByClassId(classId);
      const centerX = (x1 + x2) / 2;
      const centerY = (y1 + y2) / 2;
      const width = x2 - x1;
      const height = y2 - y1;
      const radius = Math.max(width, height) / 2;
      const suppressOutlines = false;
      ctx.beginPath();
      if ([4,6,12,14,1,2,9,5,10].includes(classId)) {
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        ctx.stroke();
      } else if (classId === 0 || classId === 11) {
        const rgbMatch = color.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
        const r = rgbMatch ? rgbMatch[1] : '0';
        const g = rgbMatch ? rgbMatch[2] : '191';
        const b = rgbMatch ? rgbMatch[3] : '255';
        const featheredRadius = radius * 1.2;
        const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, featheredRadius);
        gradient.addColorStop(0, `rgba(${r},${g},${b},0.35)`);
        gradient.addColorStop(0.45, `rgba(${r},${g},${b},0.18)`);
        gradient.addColorStop(0.75, `rgba(${r},${g},${b},0.06)`);
        gradient.addColorStop(1, `rgba(${r},${g},${b},0)`);
        const cornerRadius = Math.min(width, height) * 0.2;
        ctx.moveTo(x1 + cornerRadius, y1);
        ctx.lineTo(x2 - cornerRadius, y1);
        ctx.arcTo(x2, y1, x2, y1 + cornerRadius, cornerRadius);
        ctx.lineTo(x2, y2 - cornerRadius);
        ctx.arcTo(x2, y2, x2 - cornerRadius, y2, cornerRadius);
        ctx.lineTo(x1 + cornerRadius, y2);
        ctx.arcTo(x1, y2, x1, y2 - cornerRadius, cornerRadius);
        ctx.lineTo(x1, y1 + cornerRadius);
        ctx.arcTo(x1, y1, x1 + cornerRadius, y1, cornerRadius);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();
      } else if (classId === 3 || classId === 15) {
        const rgbMatch = color.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
        const r = rgbMatch ? rgbMatch[1] : '200';
        const g = rgbMatch ? rgbMatch[2] : '0';
        const b = rgbMatch ? rgbMatch[3] : '255';
        const offsetUp = radius * 0.22;
        const cy = centerY - offsetUp;
        const featheredRadius = radius * 1.25;
        const gradient = ctx.createRadialGradient(centerX, cy, 0, centerX, cy, featheredRadius);
        gradient.addColorStop(0, `rgba(${r},${g},${b},0.65)`);
        gradient.addColorStop(0.3, `rgba(${r},${g},${b},0.48)`);
        gradient.addColorStop(0.55, `rgba(${r},${g},${b},0.32)`);
        gradient.addColorStop(0.78, `rgba(${r},${g},${b},0.2)`);
        gradient.addColorStop(1, `rgba(${r},${g},${b},0.18)`);
        const curveBulge = radius * 0.32;
        ctx.beginPath();
        ctx.arc(centerX, cy, radius, 0, Math.PI);
        ctx.quadraticCurveTo(centerX, cy + curveBulge, centerX + radius, cy);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(centerX, cy, radius, 0, Math.PI);
        ctx.quadraticCurveTo(centerX, cy + curveBulge, centerX + radius, cy);
        ctx.closePath();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
        ctx.lineWidth = 2;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.stroke();
      } else {
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        ctx.stroke();
      }
      // Stacked outlines for clusters
      if (!suppressOutlines && stackedCount && stackedCount > 1) {
        const extra = Math.min(3, stackedCount - 1);
        for (let k = 1; k <= extra; k += 1) {
          ctx.save();
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.8;
          ctx.setLineDash([4, 3]);
          if (classId === 0 || classId === 11) {
            const pad = 3 * k;
            ctx.strokeRect(x1 - pad, y1 - pad, Math.max(1, (x2 - x1) + pad * 2), Math.max(1, (y2 - y1) + pad * 2));
          } else {
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius + (3 * k), 0, Math.PI * 2);
            ctx.stroke();
          }
          ctx.restore();
        }
      }
      // Low-confidence dashed box
      if (!suppressOutlines && det?.low_confidence) {
        ctx.save();
        ctx.setLineDash([6, 4]);
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x1, y1, Math.max(1, x2 - x1), Math.max(1, y2 - y1));
        ctx.restore();
      }
    });
  }, []);

  const renderSelectedConcernPreview = useCallback((conditionName, view = selectedConcernView) => {
    const sideResult = sideResults.find((item) => item?.key === view) || null;
    const sideImageFromResult = sideResult?.raw_image
      || sideResult?.captured_image
      || sideResult?.capturedImage
      || sideResult?.pipeline?.captured_image
      || sideResult?.marked_image
      || null;
    const sideRawImage = view === 'left'
      ? (sideCaptures?.left || sideImageFromResult)
      : view === 'right'
        ? (sideCaptures?.right || sideImageFromResult)
        : null;
    const frontBaseImage = capturedImageFromState || skinResultsFromState?.markedImage || null;
    const baseImage = view === 'front' ? frontBaseImage : sideRawImage;

    if (!conditionName) {
      setSelectedConcernPreviewImage(view === 'front' ? (skinResultsFromState?.markedImage || frontBaseImage) : sideRawImage);
      return;
    }

    const skinConditions = skinResultsFromState?.skinConditions || {};
    if (view === 'front' && isSkinConditionName(conditionName)) {
      const canonical = canonicalSkinConditionName(conditionName) || conditionName;
      const keyCandidates = getSkinConditionKeyCandidates(canonical);
      const conditionKey = keyCandidates.find((key) => (
        skinConditions?.[key]
        || conditionMaskOverrides?.[key]
      )) || keyCandidates[0] || null;
      const conditionEntry = keyCandidates
        .map((key) => skinConditions?.[key])
        .find((entry) => entry && typeof entry === 'object') || null;
      let maskUrl = (conditionKey && conditionMaskOverrides?.[conditionKey])
        || conditionEntry?.mask
        || null;
      if (typeof maskUrl === 'string' && maskUrl.startsWith('/') && API_BASE_URL) {
        maskUrl = `${API_BASE_URL.replace(/\/$/, '')}${maskUrl}`;
      }
      if (maskUrl) {
        setSelectedConcernPreviewImage(maskUrl);
        return;
      }
      if (sessionId) {
        const placeholder = frontBaseImage || skinResultsFromState?.markedImage || null;
        if (placeholder) {
          setSelectedConcernPreviewImage(placeholder);
        }
        const requestCanonical = canonical;
        fetchSkinConcernMask({
          sessionId,
          displayName: requestCanonical,
          appSessionToken: token || null,
        }).then((fetchedMask) => {
          if (!fetchedMask) return;
          const storeKey = getSkinConditionKeyCandidates(requestCanonical)[0] || requestCanonical;
          setConditionMaskOverrides((prev) => (
            prev?.[storeKey] === fetchedMask ? prev : { ...prev, [storeKey]: fetchedMask }
          ));
        }).catch(() => {});
        return;
      }
    }

    if (!baseImage) {
      setSelectedConcernPreviewImage(null);
      return;
    }

    const sideDetections = Array.isArray(sideResult?.detections)
      ? sideResult.detections
      : (Array.isArray(sideResult?.pipeline?.detections) ? sideResult.pipeline.detections : []);
    const sourceDetections = view === 'front'
      ? (skinResultsFromState?.frontDetections || skinResultsFromState?.detections || [])
      : sideDetections;
    const filteredDetections = sourceDetections.filter((det) => {
      const className = normalizeConditionName(det?.class_name || det?.className || 'Unknown');
      return className === conditionName;
    });

    if (!filteredDetections.length) {
      setSelectedConcernPreviewImage(baseImage);
      return;
    }

    const canvas = concernPreviewCanvasRef.current;
    if (!canvas) {
      setSelectedConcernPreviewImage(baseImage);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        drawConcernPreviewDetections(ctx, conditionName, filteredDetections, img.width);
        setSelectedConcernPreviewImage(canvas.toDataURL('image/jpeg', 0.95));
      } catch {
        setSelectedConcernPreviewImage(baseImage);
      }
    };
    img.onerror = () => {
      setSelectedConcernPreviewImage(baseImage);
    };
    img.src = baseImage;
  }, [API_BASE_URL, sessionId, token, selectedConcernView, sideResults, sideCaptures, capturedImageFromState, skinResultsFromState, conditionMaskOverrides, drawConcernPreviewDetections]);

  useEffect(() => {
    if (!detailedConcernItems.length) {
      setSelectedConcernName(null);
      setIsConcernTextExpanded(false);
      return;
    }
    const hasCurrentSelection = detailedConcernItems.some((item) => item.class_name === selectedConcernName);
    if (!hasCurrentSelection) {
      setSelectedConcernName(detailedConcernItems[0].class_name);
      setIsConcernTextExpanded(false);
    }
  }, [detailedConcernItems, selectedConcernName]);

  useEffect(() => {
    if (!availableConcernViews.length) return;
    if (!availableConcernViews.includes(selectedConcernView)) {
      setSelectedConcernView(availableConcernViews[0]);
    }
  }, [availableConcernViews, selectedConcernView]);

  useEffect(() => {
    if (!isSelectedSkinConditionConcern) return;
    if (selectedConcernView !== 'front') {
      setSelectedConcernView('front');
    }
  }, [isSelectedSkinConditionConcern, selectedConcernView]);

  useEffect(() => {
    renderSelectedConcernPreview(selectedConcernName, selectedConcernView);
  }, [renderSelectedConcernPreview, selectedConcernName, selectedConcernView]);

  useEffect(() => {
    if (!selectedConcernName || typeof window === 'undefined' || window.innerWidth > 768) return;
    const grid = concernGridRef.current;
    if (!grid) return;

    const selectedCard = grid.querySelector(`[data-concern-name="${selectedConcernName}"]`);
    if (!selectedCard) return;

    requestAnimationFrame(() => {
      selectedCard.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    });
  }, [selectedConcernName]);

  // InfoPopup state and content
  const [infoPopup, setInfoPopup] = useState({ open: false, key: null });
  const infoContent = {
    'concerns': {
      title: 'About Concerns',
      content: 'Skin concerns are specific issues detected in your analysis, such as hydration, oxygen, dullness, smoothness, or UnevenSkin. Tap a concern to see details, severity, and recommended actions.'
    },
    'environment': {
      title: 'Environmental Context',
      content: 'Environmental factors like UV, humidity, and pollution can affect your skin health. This section shows the current conditions at your location.'
    },
    'ai-summary': {
      title: 'AI Summary',
      content: 'A concise, AI-generated summary of your skin analysis, highlighting key findings and overall skin health.'
    },
    'ai-routine': {
      title: 'AI Routine',
      content: 'A personalized skincare routine generated by AI, tailored to your skin type, concerns, and environment. Steps are prioritized by severity.'
    },
    'products': {
      title: 'Recommended Products',
      content: 'Products suggested for your top skin concerns, based on your analysis. Tap a product for details.'
    },
    'deep-dive': {
      title: 'Analysis Deep Dive',
      content: 'Detailed explanations and insights for your most important skin concerns, including causes and improvement tips.'
    },
  };
  const openInfo = (key) => setInfoPopup({ open: true, key });
  const closeInfo = () => setInfoPopup({ open: false, key: null });

  return (
    <div className={`skin-routine-page-shell page-bg skin-only ${SKIP_AI ? 'ai-disabled' : ''}`} id="skin-routine-page-shell" ref={scrollShellRef}>
      {/* When AI is disabled we hide related UI blocks via inline styles */}
      {SKIP_AI && (
        <style>{`.skin-only.ai-disabled .skin-routine-analysis-panel, .skin-only.ai-disabled .skin-routine-ai-summary-block, .skin-only.ai-disabled .skin-routine-ai-routine-block { display: none !important; }`}</style>
      )}
      {/* Navigation Buttons */}
      <div className="skin-routine-page-nav" id="skin-routine-page-nav">
        <div className="skin-routine-page-title-wrapper">
          {segmentationAddToCart && getTotalCartItems() > 0 && (
            <button
              type="button"
              className="skin-routine-page-checkout-button-icon"
              onClick={handleCheckout}
              title={`Checkout (${getTotalCartItems()} items)`}
              aria-label={`Checkout with ${getTotalCartItems()} items`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              <span className="skin-routine-page-checkout-badge-icon">{getTotalCartItems()}</span>
            </button>
          )}
          <button
            type="button"
            className="skin-routine-page-home-button"
            onClick={handleHome}
            aria-label="Go to home"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </button>
        </div>
      </div>

      <main className="skin-routine-page-main" id="skin-routine-page-main" ref={scrollMainRef}>
        <div className="skin-routine-page-container" id="skin-routine-page-container">
          <section className="skin-routine-page-layout" id="skin-routine-page-layout">

            {/* User / Session overview card */}
            {/* Modern info content above personal info */}
            <div className="skin-routine-user-info-intro skin-text-body skin-text-medium" style={{ textAlign: 'center', margin: '0 0 1.2rem 0' }}>
             
              <span>
                This is your personalized skin analysis summary. Review your profile, score, and recommendations below for a complete overview of your skin health and routine.
              </span>
            </div>
            <div className="skin-routine-user-card" id="section-user-info">
            <div className="skin-routine-user-card-actions">
              {showPdfButton && (
              <button
                type="button"
                className="skin-routine-user-card-action-btn"
                onClick={handleGeneratePdf}
                disabled={!isPageReady || isGeneratingPdf}
                title={isGeneratingPdf ? 'Generating…' : isPageReady ? 'Generate PDF' : 'Loading…'}
                aria-label="Generate PDF"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <span className="skin-routine-user-card-action-label">{isGeneratingPdf ? 'Generating…' : 'PDF'}</span>
              </button>
              )}
            </div>
            {/* date removed per design */}
            <div className="skin-routine-user-info">
              {userImage && (
                <div className="skin-routine-user-image">
                  <img src={userImage} alt="Your skin analysis" />
                </div>
              )}
              <div className="skin-routine-user-details">
                <h2 className="skin-routine-user-name">{userName}</h2>
                <p className="skin-routine-user-subtitle">{profileSubtitle}</p>
              </div>
              {overallSkinScore != null ? (
                <div className="skin-routine-score-tile skin-routine-score-tile-inline">
                  <div className="skin-routine-score-canvas-wrapper" role="img" aria-label={`Overall skin score ${overallSkinScore} out of 100`}>
                    <canvas ref={skinGaugeCanvasRef} className="skin-routine-score-canvas" width="400" height="320" />
                    <div className="skin-routine-score-display">
                      <div className="skin-routine-score-display-value">{overallSkinScore}</div>
                      <div className="skin-routine-score-display-label">Skin Score</div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="skin-routine-report-strip">
              <div className="skin-routine-env-tile">
                <p className="routine-tile-title">Environmental Context</p>
                {aqiData && aqiData.success ? (
                  <div className="skin-routine-env-grid">
                    {aqiData.location ? (
                      <span className="skin-routine-env-item">
                        <span className="skin-routine-env-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>
                        </span>
                        <span className="skin-routine-env-label skin-text-small">Location</span>
                        <span className="skin-routine-env-value">{aqiData.location}</span>
                      </span>
                    ) : null}
                    {aqiData.temperature != null ? (
                      <span className="skin-routine-env-item">
                        <span className="skin-routine-env-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14.76V5a2 2 0 1 0-4 0v9.76a4 4 0 1 0 4 0z"/></svg>
                        </span>
                        <span className="skin-routine-env-label skin-text-small">Temp</span>
                        <span className="skin-routine-env-value">{aqiData.temperature} C</span>
                      </span>
                    ) : null}
                    {(aqiData.uv_level || aqiData.uv_index != null) ? (
                      <span className="skin-routine-env-item">
                        <span className="skin-routine-env-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M2 12h3M19 12h3M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12"/></svg>
                        </span>
                        <span className="skin-routine-env-label skin-text-small">UV Level</span>
                        <span className="skin-routine-env-value" style={{ color: getUvIndexColor(aqiData.uv_index ?? 0) }}>
                          {aqiData.uv_level || getUvDescription(aqiData.uv_index)}
                        </span>
                      </span>
                    ) : null}
                    {aqiData.humidity != null ? (
                      <span className="skin-routine-env-item">
                        <span className="skin-routine-env-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3s5 5.2 5 9a5 5 0 0 1-10 0c0-3.8 5-9 5-9z"/></svg>
                        </span>
                        <span className="skin-routine-env-label skin-text-small">Humidity</span>
                        <span className="skin-routine-env-value">{aqiData.humidity}%</span>
                      </span>
                    ) : null}
                    {aqiData.wind_speed != null ? (
                      <span className="skin-routine-env-item">
                        <span className="skin-routine-env-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h12a2 2 0 1 0-2-2"/><path d="M3 18h14a2.5 2.5 0 1 1-2.5 2.5"/><path d="M3 6h9a2 2 0 1 1-2-2"/></svg>
                        </span>
                        <span className="skin-routine-env-label skin-text-small">Wind</span>
                        <span className="skin-routine-env-value">{aqiData.wind_speed} km/h</span>
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <p className="skin-routine-score-unavailable">Environment data unavailable</p>
                )}
              </div>
            </div>
            </div>

            {/* Progressive display: 1) Concerns first, 2) Summary, 3) Routine, 4) Products */}
            {!skinResultsFromState ? (
              <div className="skin-routine-error">
                <p>Missing required data. Please go back and complete the skin analysis.</p>
                <button onClick={handleBack}>Go back</button>
              </div>
            ) : (
              <div className="skin-routine-content-wrapper skin-routine-content-no-tabs skin-routine-sections">
                {/* 1. Concerns first: show immediately */}
                {(showSkinConcerns || showSkinConditions) && (concernsList.length > 0 || skinResultsFromState?.skinScore || skinResultsFromState?.detections || skinResultsFromState?.skinConditions) && (
                  <div className="skin-routine-section-block skin-routine-concerns-block">
                    <div className="skin-routine-section-heading-with-info">
                      <h2 className="skin-routine-section-heading">Concerns</h2>
                      <button className="info-icon-btn" aria-label="About Concerns" onClick={() => openInfo('concerns')}>
                        <span className="info-icon" aria-hidden="true">
                          <img src={process.env.PUBLIC_URL + '/icon/info.png'} alt="info" className="info-icon-img" />
                        </span>
                      </button>
                    </div>
                    {detailedConcernItems.length > 0 && selectedConcernItem && (
                      <div className="skin-routine-concern-reference-block">
                        <div className="skin-routine-concern-preview-panel">
                          <div className="skin-routine-concern-preview-copy">
                            <div className="skin-routine-concern-preview-header skin-routine-concern-preview-header--copy">
                              <div>
                                <h4 className="skin-routine-concern-preview-title">{selectedConcernItem.label}</h4>
                                <div className="skin-routine-concern-preview-score-wrap skin-routine-concern-preview-score-wrap--copy" role="img" aria-label={`${selectedConcernItem.score} percent concern score`}>
                                  <div className="skin-routine-concern-gauge-wrapper">
                                    <canvas ref={concernGaugeCopyCanvasRef} className="skin-routine-concern-gauge-canvas" width="400" height="280" />
                                    <div className="skin-routine-concern-gauge-display">
                                      <div className="skin-routine-concern-gauge-label">Concern Score</div>
                                      <div className="skin-routine-concern-gauge-value">{selectedConcernItem.score}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <span className={`skin-routine-detailed-severity skin-routine-severity-${getScoreOutlineColor(selectedConcernItem.score)}`}>
                                {selectedConcernItem.score <= 39 ? 'Critical' :
                                  selectedConcernItem.score <= 59 ? 'High' :
                                  selectedConcernItem.score <= 74 ? 'Moderate' :
                                  selectedConcernItem.score <= 89 ? 'Mild' : 'Healthy'}
                              </span>
                            </div>
                            <p className="skin-routine-concern-preview-text skin-routine-concern-preview-text--copy">
                              {selectedConcernItem ? (
                                <>
                                  <strong>{selectedConcernItem.label}:</strong> {selectedConcernDescription || 'No additional description available.'}
                                </>
                              ) : (
                                'Select any concern below to inspect its focused preview and severity in the same flow as the results page.'
                              )}
                            </p>
                          </div>
                          <div className="skin-routine-concern-preview-media">
                            <div className="skin-routine-concern-preview-frame">
                              {showConcernAngleNavigation ? (
                                <>
                                  <div className="skin-routine-preview-arrow-wrap skin-routine-preview-arrow-wrap--left">
                                    <button
                                      type="button"
                                      className="skin-routine-preview-arrow"
                                      onClick={() => moveConcernPreviewBy(-1)}
                                      aria-label="View previous angle"
                                    >
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="15 18 9 12 15 6" />
                                      </svg>
                                    </button>
                                    <span className="skin-routine-preview-arrow-label">Prev</span>
                                  </div>
                                  <div className="skin-routine-preview-angle-chip">
                                    {selectedConcernView.charAt(0).toUpperCase() + selectedConcernView.slice(1)}
                                  </div>
                                  <div className="skin-routine-preview-arrow-wrap skin-routine-preview-arrow-wrap--right">
                                    <button
                                      type="button"
                                      className="skin-routine-preview-arrow"
                                      onClick={() => moveConcernPreviewBy(1)}
                                      aria-label="View next angle"
                                    >
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 18 15 12 9 6" />
                                      </svg>
                                    </button>
                                    <span className="skin-routine-preview-arrow-label">Next</span>
                                  </div>
                                </>
                              ) : null}
                              {selectedConcernPreviewImage ? (
                                <img
                                  src={selectedConcernPreviewImage}
                                  alt={`${selectedConcernItem.label} preview ${selectedConcernView}`}
                                  className="skin-routine-concern-preview-image"
                                />
                              ) : (
                                <div className="skin-routine-concern-preview-empty">Preview unavailable</div>
                              )}
                              {selectedConcernName
                                && !isSelectedSkinConditionConcern
                                && selectedConcernView === 'front' ? (
                                <FaceRegionMask
                                  imageUrl={
                                    capturedImageFromState
                                    || skinResultsFromState?.markedImage
                                    || selectedConcernPreviewImage
                                  }
                                  active
                                  showLabels={false}
                                  filled={false}
                                />
                              ) : null}
                              {selectedConcernSeverityGradientStyle ? (
                                <div
                                  key={selectedConcernName || 'severity'}
                                  className="severity-bar-gradient-only skin-routine-concern-preview-severity"
                                  style={selectedConcernSeverityGradientStyle}
                                >
                                  <div
                                    className="severity-bar-gradient"
                                    style={{ background: selectedConcernSeverityGradientStyle['--gradient-bg'] }}
                                  />
                                  <div className="severity-bar-labels">
                                    {selectedSeverityBarLabels.triple ? (
                                      <>
                                        <span>{selectedSeverityBarLabels.left}</span>
                                        <span>{selectedSeverityBarLabels.middle}</span>
                                        <span>{selectedSeverityBarLabels.right}</span>
                                      </>
                                    ) : (
                                      <>
                                        <span>{selectedSeverityBarLabels.left}</span>
                                        <span>{selectedSeverityBarLabels.right}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ) : null}
                              <canvas ref={concernPreviewCanvasRef} className="skin-routine-concern-preview-canvas" aria-hidden="true" />

                              <div className="skin-routine-concern-preview-score-wrap skin-routine-concern-preview-score-wrap--overlay" role="img" aria-label={`${selectedConcernItem.score} percent concern score`}>
                                <div className="skin-routine-concern-preview-score-head">
                                  <h4 className="skin-routine-concern-preview-title">{selectedConcernItem.label}</h4>
                                  <span className={`skin-routine-detailed-severity skin-routine-severity-${getScoreOutlineColor(selectedConcernItem.score)}`}>
                                    {selectedConcernItem.score <= 39 ? 'Critical' :
                                      selectedConcernItem.score <= 59 ? 'High' :
                                      selectedConcernItem.score <= 74 ? 'Moderate' :
                                      selectedConcernItem.score <= 89 ? 'Mild' : 'Healthy'}
                                  </span>
                                </div>
                                <div className="skin-routine-concern-mobile-compact-score" aria-hidden="true">
                                  <span className="skin-routine-concern-mobile-compact-name">{selectedConcernItem.label}</span>
                                  <span className="skin-routine-concern-mobile-compact-value">{selectedConcernItem.score}%</span>
                                </div>
                                <div className="skin-routine-concern-preview-score-row">
                                  <div className="skin-routine-concern-gauge-wrapper">
                                    <canvas ref={concernGaugeOverlayCanvasRef} className="skin-routine-concern-gauge-canvas" width="400" height="280" />
                                    <div className="skin-routine-concern-gauge-display">
                                      <div className="skin-routine-concern-gauge-label">Concern Score</div>
                                      <div className="skin-routine-concern-gauge-value">{selectedConcernItem.score}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <p
                              className="skin-routine-concern-preview-text skin-routine-concern-preview-text--below-image"
                              onClick={handleToggleConcernTextExpand}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleToggleConcernTextExpand();
                                }
                              }}
                              data-expanded={isConcernTextExpanded}
                              aria-expanded={isConcernTextExpanded}
                            >
                              {selectedConcernItem ? (
                                <>
                                  <strong>{selectedConcernItem.label}:</strong> {selectedConcernDescription || 'No additional description available.'}
                                </>
                              ) : (
                                'Select any concern below to inspect its focused preview and severity in the same flow as the results page.'
                              )}
                            </p>
                          </div>
                        </div>

                        {/* Mobile hint for concern view */}
                        <div className="skin-routine-concern-mobile-hint">
                          <p className="skin-routine-concern-mobile-hint-text">
                            Swipe left/right to view all concerns
                          </p>
                        </div>
                        <div className="skin-routine-detailed-concern-grid-wrapper">
                          <button
                            type="button"
                            className="skin-routine-concern-nav-arrow left"
                            aria-label="Scroll left"
                            onClick={() => {
                              const grid = document.querySelector('.skin-routine-detailed-concern-grid');
                              if (grid) grid.scrollBy({ left: -window.innerWidth * 0.8, behavior: 'smooth' });
                            }}
                          >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                          </button>
                          <div className="skin-routine-detailed-concern-grid" ref={concernGridRef}>
                            {detailedConcernItems.map((item, idx) => {
                              const score = Math.max(0, Math.min(100, Number(item.score || 0)));
                              const scoreCategory = getScoreOutlineColor(score);
                              const severityLabel =
                                scoreCategory === 'critical' ? 'Critical' :
                                  scoreCategory === 'poor' ? 'High' :
                                    scoreCategory === 'fair' ? 'Moderate' :
                                      scoreCategory === 'good' ? 'Mild' : 'Healthy';
                              return (
                                <div
                                  key={`detail-${item.class_name}-${idx}`}
                                  data-concern-name={item.class_name}
                                  className={`skin-routine-detailed-card score-outline-${scoreCategory}${selectedConcernName === item.class_name ? ' is-selected' : ''}${selectedConcernName === item.class_name && animatedConcernName === item.class_name ? ' is-selected-animate' : ''}`}
                                  style={{ '--selected-accent': item.color || '#38bdf8' }}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handleSelectConcern(item.class_name)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      handleSelectConcern(item.class_name);
                                    }
                                  }}
                                >
                                  <div className="skin-routine-detailed-card-top">
                                    <span className="skin-routine-detailed-name" style={{ color: item.color || '#0f766e' }}>{item.label}</span>
                                    <span className="skin-routine-detailed-score-num" style={{ color: item.color || '#0f766e' }}>{score}%</span>
                                  </div>
                                  <div className="skin-routine-detailed-card-bottom">
                                    <span className={`skin-routine-detailed-severity skin-routine-severity-${scoreCategory}`}>
                                      {severityLabel}
                                    </span>
                                    <span className="skin-routine-detailed-card-hint">Tap to preview</span>
                                  </div>
                                  <div className="skin-routine-detailed-bar-wrap">
                                    <div
                                      className="skin-routine-detailed-bar-fill"
                                      style={{ width: `${score}%`, background: item.color || '#0f766e' }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <button
                            type="button"
                            className="skin-routine-concern-nav-arrow right"
                            aria-label="Scroll right"
                            onClick={() => {
                              const grid = document.querySelector('.skin-routine-detailed-concern-grid');
                              if (grid) grid.scrollBy({ left: window.innerWidth * 0.8, behavior: 'smooth' });
                            }}
                          >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
                          </button>
                        </div>

                        <div className="skin-routine-concern-insights-menu">
                          <button
                            type="button"
                            className="skin-routine-concern-insights-toggle"
                            onClick={() => setIsConcernInsightsOpen((prev) => !prev)}
                            aria-expanded={isConcernInsightsOpen}
                            aria-controls="skin-routine-concern-insights-grid"
                          >
                            <span>Analysis Insights</span>
                            <span className={`skin-routine-concern-insights-toggle-icon${isConcernInsightsOpen ? ' is-open' : ''}`} aria-hidden="true">▾</span>
                          </button>
                        </div>

                        {isConcernInsightsOpen && (
                          <div className="skin-routine-concern-insights-grid" id="skin-routine-concern-insights-grid" aria-label="Concern explanation details">
                            <article className="skin-routine-concern-insight-card skin-routine-concern-insight-card--score">
                              <span className="skin-routine-concern-insight-eyebrow">Score meaning</span>
                              <h5 className="skin-routine-concern-insight-title">{selectedConcernSeverityInfo.level}</h5>
                              <p className="skin-routine-concern-insight-text">{selectedConcernSeverityInfo.meaning}</p>
                            </article>
                            <article className="skin-routine-concern-insight-card skin-routine-concern-insight-card--why">
                              <span className="skin-routine-concern-insight-eyebrow">Why this may happen</span>
                              <h5 className="skin-routine-concern-insight-title">Root cause insights</h5>
                              <p className="skin-routine-concern-insight-text">{selectedConcernWhyText}</p>
                            </article>
                            {selectedConcernActionProducts.length > 0 && (
                              <article className="skin-routine-concern-insight-card skin-routine-concern-insight-card--action">
                                <span className="skin-routine-concern-insight-eyebrow">What to do now</span>
                                <h5 className="skin-routine-concern-insight-title">Product-led action</h5>
                                {selectedConcernActionText ? (
                                  <p className="skin-routine-concern-insight-text">{selectedConcernActionText}</p>
                                ) : null}
                                <div className="skin-routine-concern-insight-actions" aria-label="Recommended product actions">
                                  {selectedConcernActionProducts.map((product, idx) => (
                                    <button
                                      key={`insight-product-${product.product_id || product.id || idx}`}
                                      type="button"
                                      className="skin-routine-concern-insight-action-btn"
                                      onClick={() => handleOpenProductDetailPopup(product, `Recommended for ${selectedConcernItem?.label || 'this concern'}`)}
                                    >
                                      <span className="skin-routine-concern-insight-action-name">{product.product_name || `Product ${idx + 1}`}</span>
                                      <span className="skin-routine-concern-insight-action-open">Open</span>
                                    </button>
                                  ))}
                                </div>
                              </article>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {concernsList.length === 0 && (
                      <div className="no-conditions-message">
                        <p>No significant skin conditions detected.</p>
                        <p className="no-conditions-subtitle">Your skin looks healthy!</p>
                      </div>
                    )}
                  </div>
                )}

                {showRadarAnalysisSection && concernsList.length > 0 && radarModel && activeRadarModel && (
                  <div className="skin-routine-section-block skin-routine-radar-analysis-block" id="section-radar">
                    <div className="skin-routine-section-heading-with-info">
                      <h2 className="skin-routine-section-heading">
                        <span className="skin-routine-section-heading-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                        </span>
                        Skin Analysis Report & Deep Dive
                      </h2>
                      <button className="info-icon-btn" aria-label="About Deep Dive" onClick={() => openInfo('deep-dive')}>
                        <span className="info-icon" aria-hidden="true">
                          <img src={process.env.PUBLIC_URL + '/icon/info.png'} alt="info" className="info-icon-img" />
                        </span>
                      </button>
                    </div>
                    <div className="skin-routine-section-content">
                      {canShowBeforeAfterRadar && (
                        <div className="skin-routine-radar-tabs" role="tablist" aria-label="Skin analysis report comparison tabs">
                          <button
                            type="button"
                            role="tab"
                            aria-selected={radarComparisonTab === 'before'}
                            className={`skin-routine-radar-tab ${radarComparisonTab === 'before' ? 'active' : ''}`}
                            onClick={() => setRadarComparisonTab('before')}
                          >
                            Before
                          </button>
                          <button
                            type="button"
                            role="tab"
                            aria-selected={radarComparisonTab === 'after'}
                            className={`skin-routine-radar-tab ${radarComparisonTab === 'after' ? 'active' : ''}`}
                            onClick={() => setRadarComparisonTab('after')}
                          >
                            After
                          </button>
                        </div>
                      )}

                      <div className="skin-routine-concerns-overview-grid">
                        <div className="skin-routine-radar-panel">
                          <div className="skin-routine-radar-panel-header">
                            <h3 className="skin-routine-concerns-subheading">
                              {canShowBeforeAfterRadar ? (radarComparisonTab === 'before' ? 'Before Skin Analysis Report' : 'After Skin Analysis Report') : 'Skin Analysis Report'}
                            </h3>
                          </div>
                          <div className="skin-routine-radar-meta">
                            {isAfterComparisonView ? (
                              <>
                                <span className="skin-routine-radar-meta-score">
                                  Score: {overallSkinScore != null ? `${overallSkinScore}/100` : '--'}
                                </span>
                                <span className="skin-routine-radar-meta-date">
                                  From {previousAnalysisDateText || '--'} to {analysisDateText || '--'}
                                  {radarDaysBetween != null ? ` (${radarDaysBetween} day${radarDaysBetween === 1 ? '' : 's'})` : ''}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="skin-routine-radar-meta-score">
                                  Score: {activeRadarScore != null ? `${activeRadarScore}/100` : '--'}
                                </span>
                                <span className="skin-routine-radar-meta-date">
                                  {canShowBeforeAfterRadar && radarComparisonTab === 'before' ? 'Before Date: ' : 'Date: '}
                                  {activeRadarDate || '--'}
                                </span>
                              </>
                            )}
                          </div>
                          <svg className="skin-routine-radar-svg" viewBox={`0 0 ${activeRadarModel.size} ${activeRadarModel.size}`} role="img" aria-label="Skin analysis report chart">
                            {activeRadarModel.levels.map((level) => {
                              const r = (level / 100) * activeRadarModel.radius;
                              return (
                                <circle
                                  key={`level-${level}`}
                                  cx={activeRadarModel.cx}
                                  cy={activeRadarModel.cy}
                                  r={r}
                                  fill="none"
                                  stroke="rgba(100, 116, 139, 0.22)"
                                  strokeWidth="1"
                                />
                              );
                            })}
                            {activeRadarModel.axis.map((item, idx) => (
                              <line
                                key={`axis-${idx}`}
                                x1={activeRadarModel.cx}
                                y1={activeRadarModel.cy}
                                x2={activeRadarModel.cx + Math.cos(item.angle) * activeRadarModel.radius}
                                y2={activeRadarModel.cy + Math.sin(item.angle) * activeRadarModel.radius}
                                stroke="rgba(100, 116, 139, 0.2)"
                                strokeWidth="1"
                              />
                            ))}
                            {isAfterComparisonView ? (
                              <>
                                <polygon
                                  points={activeRadarModel.beforePolygon}
                                  fill="none"
                                  stroke="rgba(249, 115, 22, 0.95)"
                                  strokeWidth="1.8"
                                  strokeDasharray="5 4"
                                />
                                <polygon
                                  points={activeRadarModel.afterPolygon}
                                  fill="none"
                                  stroke="rgba(37, 99, 235, 0.95)"
                                  strokeWidth="2.2"
                                />
                                {activeRadarModel.axis.map((item, idx) => (
                                  <g
                                    key={`compare-point-${idx}`}
                                    className="skin-routine-radar-clickable"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openRadarConcernModal(idx)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openRadarConcernModal(idx);
                                      }
                                    }}
                                  >
                                    {item.hasBefore && item.hasAfter ? (
                                      <line
                                        x1={item.beforePointX}
                                        y1={item.beforePointY}
                                        x2={item.afterPointX}
                                        y2={item.afterPointY}
                                        stroke={
                                          item.changeKind === 'improved' ? 'rgba(22, 163, 74, 0.95)'
                                            : item.changeKind === 'declined' ? 'rgba(220, 38, 38, 0.95)'
                                              : 'rgba(148, 163, 184, 0.9)'
                                        }
                                        strokeWidth="2"
                                        strokeDasharray={item.changeKind === 'stable' ? '3 3' : 'none'}
                                      />
                                    ) : null}
                                    {item.hasBefore ? <circle cx={item.beforePointX} cy={item.beforePointY} r="2.8" fill="rgba(249, 115, 22, 0.95)" /> : null}
                                    {item.hasAfter ? (
                                      <circle
                                        cx={item.afterPointX}
                                        cy={item.afterPointY}
                                        r="3.8"
                                        fill={
                                          item.changeKind === 'new' ? 'rgba(168, 85, 247, 0.98)'
                                            : item.changeKind === 'improved' ? 'rgba(22, 163, 74, 0.98)'
                                              : item.changeKind === 'declined' ? 'rgba(220, 38, 38, 0.98)'
                                                : 'rgba(37, 99, 235, 0.95)'
                                        }
                                      />
                                    ) : null}
                                    <text
                                      x={item.labelX + (item.labelOffsetX || 0)}
                                      y={item.labelY}
                                      textAnchor={item.labelAnchor || 'middle'}
                                      className={`skin-routine-radar-label skin-routine-radar-label--${item.changeKind}`}
                                    >
                                      {item.label}
                                    </text>
                                    {/* condition scores hidden per user request */}
                                  </g>
                                ))}
                              </>
                            ) : (
                              <>
                                <polygon
                                  points={activeRadarModel.polygon}
                                  fill="none"
                                  stroke="rgba(37, 99, 235, 0.95)"
                                  strokeWidth="2"
                                />
                                {activeRadarModel.axis.map((item, idx) => (
                                  <g
                                    key={`point-${idx}`}
                                    className="skin-routine-radar-clickable"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openRadarConcernModal(idx)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openRadarConcernModal(idx);
                                      }
                                    }}
                                  >
                                    <circle cx={item.pointX} cy={item.pointY} r="3.6" fill="rgba(37, 99, 235, 0.95)" />
                                    <text
                                      x={item.labelX + (item.labelOffsetX || 0)}
                                      y={item.labelY}
                                      textAnchor={item.labelAnchor || 'middle'}
                                      className="skin-routine-radar-label"
                                    >
                                      {item.label}
                                    </text>
                                    {/* condition score hidden per user request */}
                                  </g>
                                ))}
                              </>
                            )}
                          </svg>
                          <div className="skin-routine-radar-legend" aria-label="Radar chart color legend">
                            {isAfterComparisonView ? (
                              <>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--before" />Before line</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />After line</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--improved" />Improved</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--declined" />Needs attention</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--new" />New in after</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--stable" />Stable</span>
                              </>
                            ) : (
                              <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />Current scan line</span>
                            )}
                          </div>
                        </div>

                        <div className="skin-routine-analysis-panel">
                          <div className="skin-routine-section-heading-with-info">
                            <h3 className="skin-routine-concerns-subheading">Analysis Deep Dive</h3>
                            <button className="info-icon-btn" aria-label="About Deep Dive" onClick={() => openInfo('deep-dive')}>
                              <span className="info-icon" aria-hidden="true">
                                <img src={process.env.PUBLIC_URL + '/icon/info.png'} alt="info" className="info-icon-img" />
                              </span>
                            </button>
                          </div>
                          {isLoadingConditionDescriptions ? (
                            <div className="skin-routine-summary-loading">
                              <div className="loading-spinner"></div>
                              <p className="skin-routine-summary-loading-text">Generating detailed analysis...</p>
                            </div>
                          ) : deepDiveLines.length > 0 ? (
                            <div className="skin-routine-analysis-lines">
                              {deepDiveLines.map((line, idx) => (
                                <div key={`deep-line-${idx}`} className="skin-routine-analysis-line-item">
                                  <span className="skin-routine-analysis-line-bullet" aria-hidden="true">✦</span>
                                  <p className="skin-routine-analysis-line-text">{line}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="skin-routine-analysis-empty">No high or moderate concerns require deep-dive analysis right now.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 2. AI Summary */}
                {showSummaryAndInsights && (
                  <div className="skin-routine-section-block skin-routine-ai-summary-block">
                    <div className="skin-routine-section-heading-with-info">
                      <h2 className="skin-routine-section-heading">AI Summary</h2>
                      <button className="info-icon-btn" aria-label="About AI Summary" onClick={() => openInfo('ai-summary')}>
                        <span className="info-icon" aria-hidden="true">
                          <img src={process.env.PUBLIC_URL + '/icon/info.png'} alt="info" className="info-icon-img" />
                        </span>
                      </button>
                    </div>
                    <div className="skin-routine-section-content">
                      <div className="skin-routine-summary-box">
                        {summaryLoading ? (
                          <div className="skin-routine-summary-loading">
                            <div className="loading-spinner"></div>
                            <p className="skin-routine-summary-loading-text">Generating summary...</p>
                          </div>
                        ) : error ? (
                          <div className="skin-routine-summary-error-wrap">
                            <p className="skin-routine-summary-text skin-routine-summary-error">{error}</p>
                            <button type="button" className="skin-action-button skin-action-button-secondary" onClick={() => window.location.reload()}>Retry</button>
                          </div>
                        ) : showFallbackSummary ? (
                          <p className="skin-routine-summary-text">{FALLBACK_SUMMARY}</p>
                        ) : summary ? (
                          <div
                            className="skin-routine-summary-text"
                            dangerouslySetInnerHTML={{ __html: formatAiSummaryHtml(summary) }}
                          />
                        ) : (
                          <p className="skin-routine-summary-text">{FALLBACK_SUMMARY}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* 3. AI Routine */}
                <div className="skin-routine-section-block skin-routine-ai-routine-block">
                  <div className="skin-routine-section-heading-with-info">
                    <h2 className="skin-routine-section-heading">AI Routine</h2>
                    <button className="info-icon-btn" aria-label="About AI Routine" onClick={() => openInfo('ai-routine')}>
                      <span className="info-icon" aria-hidden="true">
                        <img src={process.env.PUBLIC_URL + '/icon/info.png'} alt="info" className="info-icon-img" />
                      </span>
                    </button>
                  </div>
                  <div className="skin-routine-section-content">
                    {routineLoading && routine.morning.length === 0 && routine.evening.length === 0 ? (
                      <div className="skin-routine-routine-loading">
                        <div className="loading-spinner"></div>
                        <p className="skin-routine-routine-loading-text">Generating your personalized routine...</p>
                      </div>
                    ) : error && routine.morning.length === 0 && routine.evening.length === 0 ? (
                      <div className="skin-routine-routine-error-wrap">
                        <p className="skin-routine-routine-error">{error}</p>
                        <button type="button" className="skin-action-button skin-action-button-secondary" onClick={() => window.location.reload()}>Retry</button>
                      </div>
                    ) : (routine.morning.length > 0 || routine.evening.length > 0) ? (
                      <>
                      {routine.morning.length > 0 && (
                        <div className="skin-routine-time-block">
                          <h3 className="skin-routine-time-title">
                            <img src={`${process.env.PUBLIC_URL}/icon/mor.png`} alt="" className="skin-routine-time-title-icon" aria-hidden="true" />
                            <span>Morning Routine</span>
                          </h3>
                          <ol className="skin-routine-steps-list">
                            {routine.morning.map((step, index) => (
                              <li key={index} className="skin-routine-step">{step}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                      {routine.evening.length > 0 && (
                        <div className="skin-routine-time-block">
                          <h3 className="skin-routine-time-title">
                            <img src={`${process.env.PUBLIC_URL}/icon/eve.png`} alt="" className="skin-routine-time-title-icon" aria-hidden="true" />
                            <span>Evening Routine</span>
                          </h3>
                          <ol className="skin-routine-steps-list">
                            {routine.evening.map((step, index) => (
                              <li key={index} className="skin-routine-step">{step}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                      {skincareTips.length > 0 && (
                        <div className="skin-routine-tips-block">
                          <h3 className="skin-routine-time-title skin-routine-tips-title">
                            <span>Skincare Tips</span>
                          </h3>
                          <ul className="skin-routine-tips-list">
                            {skincareTips.map((tip, index) => (
                              <li key={index} className="skin-routine-tip-item">{tip}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      </>
                    ) : null}
                  </div>
                </div>

                {/* 4. Recommended products with cart + checkout */}
                {segmentationConfigLoaded && productSuggestionEnabled && (
                <div className="skin-routine-section-block skin-routine-products-block">
                  <div className="skin-routine-section-heading-with-info">
                    <h2 className="skin-routine-section-heading">Recommended Products</h2>
                    <button className="info-icon-btn" aria-label="About Recommended Products" onClick={() => openInfo('products')}>
                      <span className="info-icon" aria-hidden="true">
                        <img src={process.env.PUBLIC_URL + '/icon/info.png'} alt="info" className="info-icon-img" />
                      </span>
                    </button>
                  </div>
                  {productsLoading ? (
                    <div className="skin-routine-products-loading">
                      <div className="loading-spinner"></div>
                      <p>Loading recommendations...</p>
                    </div>
                  ) : productsError ? (
                    <div className="skin-routine-products-error">
                      <p>{productsError}</p>
                    </div>
                  ) : recommendations?.recommended_products ? (() => {
                    const productsList = flattenRecommendedProducts(recommendations.recommended_products);
                    return (
                      <div className="skin-routine-product-recommendations-content product-recommendations-content">
                        <div className="recommended-live-product-grid">
                          {productsList && productsList.length > 0 ? (
                            productsList.map((product, idx) => {
                              const productId = product.product_id || product.id || product.sku || `prod_${(product.product_name || product.name || '').replace(/\s+/g, '_')}_${idx}`;
                              const cartQuantity = cartItems[productId]?.quantity || 0;
                              return (
                                <RecommendedProductCard
                                  key={productId}
                                  product={product}
                                  productId={productId}
                                  cartQuantity={cartQuantity}
                                  showAddToCart={segmentationAddToCart}
                                  showProductLink={segmentationProductLink}
                                  onAddToCart={handleAddToCart}
                                  onViewProduct={handleViewProduct}
                                  storeUrl={productsPageUrl}
                                />
                              );
                            })
                          ) : <div className="no-products-message"><p>No products found.</p></div>}
                        </div>
                      </div>
                    );
                  })() : (
                    <div className="no-products-message"><p>No recommendations yet.</p></div>
                  )}
                </div>
                )}

                {/* 5. Actions: Checkout */}
                {segmentationAddToCart ? (
                  <div className="skin-routine-page-actions" id="skin-routine-page-actions">
                    <div className="skin-routine-page-actions-buttons">
                      <button
                        type="button"
                        className="skin-action-button skin-routine-checkout-button"
                        onClick={handleCheckout}
                        disabled={!isPageReady}
                        title={isPageReady ? 'Checkout' : 'Loading…'}
                      >
                        Checkout
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </section>
        </div>
      </main>

      {/* Home confirmation popup */}
      {/* InfoPopup for contextual help */}
      <InfoPopup
        open={infoPopup.open}
        onClose={closeInfo}
        title={infoContent[infoPopup.key]?.title}
      >
        {infoContent[infoPopup.key]?.content}
      </InfoPopup>

      {showHomeConfirmPopup && (
        <div className="skin-routine-home-confirm-overlay" onClick={handleCancelHomeNavigation} role="dialog" aria-modal="true" aria-labelledby="home-confirm-title">
          <div className="skin-routine-home-confirm-popup" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="skin-routine-home-confirm-close" onClick={handleCancelHomeNavigation} aria-label="Close">×</button>
            <div className="skin-routine-home-confirm-icon-wrap" aria-hidden="true">
              <svg className="skin-routine-home-confirm-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 11.5L12 4l9 7.5" />
                <path d="M5 10.8V21h14V10.8" />
                <path d="M9.5 21v-6h5v6" />
              </svg>
            </div>
            <h3 className="skin-routine-home-confirm-title" id="home-confirm-title">Exit this section?</h3>
            <p className="skin-routine-home-confirm-message">Your current progress will be closed, and you will return to the home screen.</p>
            <div className="skin-routine-home-confirm-actions">
              <button type="button" className="skin-action-button skin-action-button-secondary skin-routine-home-confirm-cancel" onClick={handleCancelHomeNavigation}>
                Stay here
              </button>
              <button type="button" className="skin-action-button skin-routine-home-confirm-ok" onClick={handleConfirmHomeNavigation}>
                Go home
              </button>
            </div>
          </div>
        </div>
      )}

      {/* USP Popup */}
      {uspPopup.open && (() => {
        const raw = String(uspPopup.text);
        const isHtml = /<[a-z][\s\S]*>/i.test(raw);
        const html = isHtml ? sanitizeHtml(raw) : sanitizeHtml(raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n/g, '<br />'));
        return (
          <div className="product-usp-popup-overlay" role="dialog" aria-modal="true" onClick={handleCloseUspPopup}>
            <div className="product-usp-popup-modal" onClick={e => e.stopPropagation()}>
              <button className="product-usp-popup-close" onClick={handleCloseUspPopup} aria-label="Close">×</button>
              {uspPopup.heading && <h3 className="product-usp-popup-heading skin-text-h3">{uspPopup.heading}</h3>}
              <div className="product-usp-popup-text skin-text-body" dangerouslySetInnerHTML={{ __html: html }} />
            </div>
          </div>
        );
      })()}

      {productDetailPopup.open && productDetailPopup.product && (() => {
        const product = productDetailPopup.product;
        const productTypeDisplay = product.product_type || product.productType || (Array.isArray(product.product_types) && product.product_types?.[0]) || product.product_types;
        const recommendationLine = productDetailPopup.recommendationLine || '';
        const majorUspText = product.major_usp ? stripHtml(product.major_usp) : '';
        const productId = product.product_id || product.id || product.sku || `prod_${product.product_name || 'product'}`;
        const cartQuantity = cartItems[productId]?.quantity || 0;
        return (
          <div className="skin-routine-product-modal-overlay" onClick={handleCloseProductDetailPopup}>
            <div className="skin-routine-product-modal" role="dialog" aria-modal="true" aria-label="Product details" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="skin-routine-product-modal-close" onClick={handleCloseProductDetailPopup} aria-label="Close product details">×</button>
              <div className="skin-routine-product-modal-body">
                <div className="product-card skin-routine-product-modal-card">
                  {recommendationLine ? <div className="product-recommendation-badge">{recommendationLine}</div> : null}
                  <div className="product-card-image">
                    {product.image_url && <img src={product.image_url} alt={product.product_name || 'Product'} onError={(e) => { e.target.style.display = 'none'; }} />}
                  </div>
                  <div className="product-info">
                    <div className="product-name-header">
                      {product.product_link ? (
                        <a
                          className="product-name product-name-link"
                          href={product.product_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {product.product_name || 'Product'}
                        </a>
                      ) : (
                        <span className="product-name">{product.product_name || 'Product'}</span>
                      )}
                    </div>
                    {productTypeDisplay && (
                      <div className="product-type-heading">Type: {productTypeDisplay}</div>
                    )}
                    {majorUspText && (
                      <div className="product-major-usp-wrapper">
                        <span className="product-card-usp-heading">Key benefit</span>
                        <span className="product-major-usp-text skin-routine-product-modal-inline-text">{majorUspText}</span>
                      </div>
                    )}
                    {Array.isArray(product.concerns_treated) && product.concerns_treated.length > 0 && (
                      <div className="product-info-section">
                        <div className="product-info-label"><img src={`${process.env.PUBLIC_URL}/icon/treat.png`} alt="Treats" className="product-info-icon" /><span>Treats</span></div>
                        <div className="product-tags-container">{product.concerns_treated.map((concern, i) => <span key={`modal-concern-${i}`} className="product-tag">{concern}</span>)}</div>
                      </div>
                    )}
                  </div>
                  <div className="product-card-footer">
                    <div className="product-price-quantity">
                      <span className="product-price">{formatProductPrice(product.price)}</span>
                    </div>
                    {segmentationAddToCart ? (
                      <ProductCartCounter
                        key={`popup-counter-${productId}`}
                        product={product}
                        onAddToCart={(nextProduct, quantity) => handleAddToCart(nextProduct, quantity, productId)}
                        initialQuantity={cartQuantity}
                      />
                    ) : null}
                    {segmentationProductLink && isValidProductUrl(product.product_link) ? (
                      <button
                        type="button"
                        className="skin-action-button product-link-button"
                        onClick={(e) => { e.stopPropagation(); handleViewProduct(product, product.product_link); }}
                        title="Open product page"
                      >
                        View product
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <CheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={handleCloseCheckoutModal}
        cartItems={Object.entries(cartItems)
          .filter(([_, item]) => item.quantity > 0)
          .map(([productId, item]) => ({ ...item, productId }))}
        onCartUpdate={handleCartUpdate}
        sessionId={sessionId}
        apiBaseUrl={API_BASE_URL}
        token={token}
        customerDetails={{
          name: userDataFromState?.name || userDataFromState?.Name || '',
          phone: userDataFromState?.mobileNumber || userDataFromState?.mobile_number || '',
          email: userDataFromState?.email || userDataFromState?.Email || '',
        }}
      />

      {showRadarAnalysisSection && radarConcernModal.open && activeRadarConcernDetail && (
        <div className="skin-routine-radar-modal-backdrop" onClick={closeRadarConcernModal}>
          <div
            className="skin-routine-radar-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Concern comparison details"
            onTouchStart={(e) => {
              radarModalTouchStartXRef.current = e.touches?.[0]?.clientX ?? null;
            }}
            onTouchEnd={(e) => {
              const startX = radarModalTouchStartXRef.current;
              const endX = e.changedTouches?.[0]?.clientX ?? null;
              radarModalTouchStartXRef.current = null;
              if (startX == null || endX == null) return;
              const diff = endX - startX;
              if (Math.abs(diff) < 32) return;
              if (diff < 0) stepRadarConcernModal(1);
              else stepRadarConcernModal(-1);
            }}
          >
            <div className="skin-routine-radar-modal-header">
              <h3 className="skin-routine-radar-modal-title">{activeRadarConcernDetail.label}</h3>
              <button type="button" className="skin-routine-radar-modal-close" onClick={closeRadarConcernModal} aria-label="Close concern comparison modal">x</button>
            </div>
            <p className="skin-routine-radar-modal-subtitle">{canShowBeforeAfterRadar ? 'Before vs after progression' : 'Current concern snapshot'}</p>
            <p className="skin-routine-radar-modal-dates">
              {canShowBeforeAfterRadar
                ? `${previousAnalysisDateText || '--'} -> ${analysisDateText || '--'}`
                : (analysisDateText || '--')}
            </p>
            <svg className="skin-routine-radar-modal-linechart" viewBox="0 0 320 160" role="img" aria-label="Concern line comparison chart">
              <rect x="40" y="20" width="250" height="112" fill="rgba(148,163,184,0.08)" rx="10" />
              {[0, 25, 50, 75, 100].map((tick) => (
                <g key={`tick-${tick}`}>
                  <line x1="40" y1={132 - (tick / 100) * 112} x2="290" y2={132 - (tick / 100) * 112} stroke="rgba(148,163,184,0.22)" strokeWidth="1" />
                  <text x="34" y={136 - (tick / 100) * 112} textAnchor="end" className="skin-routine-radar-modal-axis">{tick}</text>
                </g>
              ))}
              {activeRadarConcernDetail.beforeScore != null && activeRadarConcernDetail.afterScore != null ? (
                <>
                  <line
                    x1="90"
                    y1={132 - (activeRadarConcernDetail.beforeScore / 100) * 108}
                    x2="240"
                    y2={132 - (activeRadarConcernDetail.afterScore / 100) * 108}
                    stroke={activeRadarConcernDetail.delta != null && activeRadarConcernDetail.delta >= 0 ? 'rgba(22,163,74,0.95)' : 'rgba(220,38,38,0.95)'}
                    strokeWidth="3"
                  />
                  <circle cx="90" cy={132 - (activeRadarConcernDetail.beforeScore / 100) * 108} r="5" fill="rgba(71, 85, 105, 0.95)" />
                  <circle cx="240" cy={132 - (activeRadarConcernDetail.afterScore / 100) * 108} r="5" fill="rgba(15, 118, 110, 0.95)" />
                </>
              ) : activeRadarConcernDetail.afterScore != null ? (
                <circle cx="240" cy={132 - (activeRadarConcernDetail.afterScore / 100) * 108} r="5" fill="rgba(15, 118, 110, 0.95)" />
              ) : (
                <circle cx="90" cy={132 - ((activeRadarConcernDetail.beforeScore || 0) / 100) * 108} r="5" fill="rgba(71, 85, 105, 0.95)" />
              )}
              <text x="90" y="148" textAnchor="middle" className="skin-routine-radar-modal-axis">{canShowBeforeAfterRadar ? (previousAnalysisDateText || 'Before') : '--'}</text>
              <text x="240" y="148" textAnchor="middle" className="skin-routine-radar-modal-axis">{analysisDateText || (canShowBeforeAfterRadar ? 'After' : 'Current')}</text>
            </svg>
            <div className="skin-routine-radar-modal-legend">
              {canShowBeforeAfterRadar && <span className="skin-routine-radar-modal-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--before" />Before</span>}
              <span className="skin-routine-radar-modal-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />After</span>
              {canShowBeforeAfterRadar && <span className="skin-routine-radar-modal-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--improved" />Improved line</span>}
              {canShowBeforeAfterRadar && <span className="skin-routine-radar-modal-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--declined" />Decline line</span>}
            </div>
            <div className="skin-routine-radar-modal-actions">
              <button type="button" className="skin-routine-radar-modal-nav" onClick={() => stepRadarConcernModal(-1)}>Prev</button>
              <button type="button" className="skin-routine-radar-modal-nav" onClick={() => stepRadarConcernModal(1)}>Next</button>
            </div>
          </div>
        </div>
      )}

      {showBackToTop && (
        <button
          type="button"
          className="skin-routine-back-to-top"
          onClick={handleScrollToTop}
          aria-label="Back to top"
          title="Back to top"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 15l-6-6-6 6" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default SkinRoutinePage;
